/* mo-lineup-serta.js: the Serta look of sections/mo-brand-lineup.liquid (2026-10-05).
 * Loaded only when the section's Look is "Serta". Registers:
 *   MOLineupHeroes.turndown  the hero showpiece: a Queen pillow top, made up with a sheet whose
 *                            corner is turned down. The sheet is a cloth simulation; pull it by any
 *                            edge, or tap the bed to make it up and turn it down again.
 *   MOLineupDraw.serta       the row drawings: a side view with the cover cut back, in Serta's
 *                            materials, each layer at the maker's published depth.
 *   the side-by-side strip   every model standing on one floor line, height to scale. Coil beds
 *                            spring back under the pointer; foam beds sink and take their time.
 * No libraries. Reduced motion: one still frame of each, nothing moves.
 * The sheet: Verlet integration (Jakobsen 2001) with distance constraints, after "Cloth
 * Simulation" by ln-dev7 and "Woven Cloth" by mengto (21st.dev), rebuilt in plain WebGL around a
 * mattress, with collisions, friction and a two-layer contact so a folded sheet stays folded. */
(function () {
  'use strict';
  if (window.MOLineupSerta) return;
  window.MOLineupSerta = true;

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var heroes = window.MOLineupHeroes = window.MOLineupHeroes || {};
  var draws = window.MOLineupDraw = window.MOLineupDraw || {};

  /* colors: parsed by a detached 2D canvas, so any CSS color works and nothing touches the page */
  var probe = null;
  function rgb(value, fallback) {
    value = (value || '').trim();
    if (!value) return fallback;
    if (!probe) { probe = document.createElement('canvas').getContext('2d'); }
    if (!probe) return fallback;
    probe.fillStyle = '#010203';
    probe.fillStyle = value;
    var s = probe.fillStyle;
    if (s === '#010203' && value.toLowerCase() !== '#010203') return fallback;
    var m;
    if (s.charAt(0) === '#') return [parseInt(s.substr(1, 2), 16) / 255, parseInt(s.substr(3, 2), 16) / 255, parseInt(s.substr(5, 2), 16) / 255];
    if ((m = /rgba?\(([^)]+)\)/.exec(s))) { var p = m[1].split(',').map(parseFloat); return [p[0] / 255, p[1] / 255, p[2] / 255]; }
    return fallback;
  }

  /* ==================================================================================
   * The turndown: scene, in metres. The bed's long side runs along x (head at -x), its width
   * along z (the near side, toward the viewer, at +z). A Queen is 60 x 80 in.; this one is a
   * 16 in. pillow top: a body and a crown, with two pillows at the head.
   * ================================================================================== */
  var BED = { hl: 1.016, hw: 0.762, T: 0.406, crown: 0.1 };
  var PILLOW = { x: -0.80, z: 0.36, hx: 0.18, hy: 0.075, hz: 0.335 };
  /* the sheet: from just below the pillows to past the foot, past both sides */
  var SHEET = { x0: -0.585, x1: BED.hl + 0.17, zh: BED.hw + 0.17, step: 0.037, th: 0.011 };
  var G = 9.81;

  /* ---------- small math ---------- */
  function v3(x, y, z) { return [x, y, z]; }
  function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
  function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
  function cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
  function norm(a) { var l = Math.sqrt(dot(a, a)) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lookAt(eye, at, up) {
    var f = norm(sub(at, eye)), s = norm(cross(f, up)), u = cross(s, f);
    return [s[0], u[0], -f[0], 0, s[1], u[1], -f[1], 0, s[2], u[2], -f[2], 0, -dot(s, eye), -dot(u, eye), dot(f, eye), 1];
  }
  function persp(fovy, aspect, near, far) {
    var f = 1 / Math.tan(fovy / 2), nf = 1 / (near - far);
    return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
  }
  function mul(a, b) {
    var o = new Array(16), i, j;
    for (i = 0; i < 4; i++) for (j = 0; j < 4; j++) o[j * 4 + i] = a[i] * b[j * 4] + a[4 + i] * b[j * 4 + 1] + a[8 + i] * b[j * 4 + 2] + a[12 + i] * b[j * 4 + 3];
    return o;
  }
  function xform(m, p) {
    var x = p[0], y = p[1], z = p[2];
    var w = m[3] * x + m[7] * y + m[11] * z + m[15];
    return [(m[0] * x + m[4] * y + m[8] * z + m[12]) / w, (m[1] * x + m[5] * y + m[9] * z + m[13]) / w, w];
  }

  /* ---------- meshes ---------- */
  /* A rounded box: a subdivided cube whose vertices are pulled onto an inner box plus a radius.
     Extra ticks sit in the rounded band so the edges stay smooth. mat(): material per vertex. */
  function ticks(h, r, k, m) {
    var a = (h - r) / h, out = [], i;
    for (i = 0; i < k; i++) out.push(-1 + (1 - a) * i / k);
    for (i = 0; i <= m; i++) out.push(-a + 2 * a * i / m);
    for (i = 1; i <= k; i++) out.push(a + (1 - a) * i / k);
    return out;
  }
  function roundedBox(mesh, c, H, r, mat, shape) {
    var tx = ticks(H[0], r, 5, 18), ty = ticks(H[1], r, 5, 4), tz = ticks(H[2], r, 5, 14);
    var faces = [
      [0, 1, 2, 1, tx, tz], [0, 1, 2, -1, tx, tz],   /* top, bottom: axis y */
      [0, 2, 1, 1, tx, ty], [0, 2, 1, -1, tx, ty],   /* near, far: axis z */
      [2, 0, 1, 1, tz, ty], [2, 0, 1, -1, tz, ty]    /* foot, head: axis x */
    ];
    faces.forEach(function (f) {
      var ua = f[0], na = f[1], va = f[2], sgn = f[3], tu = f[4], tv = f[5];
      var base = mesh.pos.length / 3, i, j;
      for (j = 0; j < tv.length; j++) {
        for (i = 0; i < tu.length; i++) {
          var cc = [0, 0, 0];
          cc[ua] = tu[i]; cc[va] = tv[j]; cc[na] = sgn;
          var P = [cc[0] * H[0], cc[1] * H[1], cc[2] * H[2]];
          var inner = [clamp(P[0], -(H[0] - r), H[0] - r), clamp(P[1], -(H[1] - r), H[1] - r), clamp(P[2], -(H[2] - r), H[2] - r)];
          var d = sub(P, inner), n = norm(d);
          var p = [inner[0] + n[0] * r, inner[1] + n[1] * r, inner[2] + n[2] * r];
          if (shape) p = shape(p, n);
          mesh.pos.push(c[0] + p[0], c[1] + p[1], c[2] + p[2]);
          mesh.mat.push(mat(p, n));
        }
      }
      var W = tu.length;
      /* wind every face outward, so normals computed from the triangles point out */
      var eu = [0, 0, 0], ev = [0, 0, 0], en = [0, 0, 0];
      eu[ua] = 1; ev[va] = 1; en[na] = sgn;
      var flip = dot(cross(eu, ev), en) < 0;
      for (j = 0; j < tv.length - 1; j++) for (i = 0; i < W - 1; i++) {
        var a = base + j * W + i, b = a + 1, cI = a + W, dI = cI + 1;
        if (flip) mesh.idx.push(a, dI, b, a, cI, dI);
        else mesh.idx.push(a, b, dI, a, dI, cI);
      }
    });
  }
  /* normals from the triangles, so deformed shapes shade correctly; points that share a position
     (the seams between a box's faces) share one normal, so edges stay smooth */
  function normals(pos, idx, out, weld) {
    var n = out || new Float32Array(pos.length), i;
    for (i = 0; i < n.length; i++) n[i] = 0;
    for (i = 0; i < idx.length; i += 3) {
      var a = idx[i] * 3, b = idx[i + 1] * 3, c = idx[i + 2] * 3;
      var ux = pos[b] - pos[a], uy = pos[b + 1] - pos[a + 1], uz = pos[b + 2] - pos[a + 2];
      var wx = pos[c] - pos[a], wy = pos[c + 1] - pos[a + 1], wz = pos[c + 2] - pos[a + 2];
      var nx = uy * wz - uz * wy, ny = uz * wx - ux * wz, nz = ux * wy - uy * wx;
      n[a] += nx; n[a + 1] += ny; n[a + 2] += nz; n[b] += nx; n[b + 1] += ny; n[b + 2] += nz; n[c] += nx; n[c + 1] += ny; n[c + 2] += nz;
    }
    if (weld) {
      var map = {}, key, k;
      for (i = 0; i < n.length; i += 3) {
        key = Math.round(pos[i] * 2e4) + ',' + Math.round(pos[i + 1] * 2e4) + ',' + Math.round(pos[i + 2] * 2e4);
        k = map[key];
        if (k == null) map[key] = [i];
        else k.push(i);
      }
      Object.keys(map).forEach(function (key2) {
        var list = map[key2];
        if (list.length < 2) return;
        var sx = 0, sy = 0, sz = 0;
        list.forEach(function (j) { sx += n[j]; sy += n[j + 1]; sz += n[j + 2]; });
        list.forEach(function (j) { n[j] = sx; n[j + 1] = sy; n[j + 2] = sz; });
      });
    }
    for (i = 0; i < n.length; i += 3) {
      var l = Math.sqrt(n[i] * n[i] + n[i + 1] * n[i + 1] + n[i + 2] * n[i + 2]) || 1;
      n[i] /= l; n[i + 1] /= l; n[i + 2] /= l;
    }
    return n;
  }
  /* one material per piece (1 crown, 2 border, 3 pillow); the shader splits each by position and
     normal, so a value is never interpolated across a boundary */
  function buildBed() {
    var m = { pos: [], mat: [], idx: [] };
    var bodyH = BED.T - BED.crown + 0.016, crownY0 = BED.T - BED.crown;
    /* the body: the coil unit inside its border */
    roundedBox(m, [0, bodyH / 2, 0], [BED.hl, bodyH / 2, BED.hw], 0.022, function () { return 2; });
    /* the crown: a pillow top sewn on, a little inset, with soft rounded sides that bulge */
    var ch = BED.crown / 2;
    roundedBox(m, [0, crownY0 + ch, 0], [BED.hl - 0.012, ch, BED.hw - 0.012], 0.024, function () { return 1; }, function (p, n) {
      var t = clamp((p[1] + ch) / (2 * ch), 0, 1), b = Math.sin(Math.PI * t) * 0.011;
      if (Math.abs(n[1]) < 0.6) { p[0] += n[0] * b; p[2] += n[2] * b; }
      /* a slight crown: the middle of the top sits a touch higher than the edges */
      if (n[1] > 0.55) p[1] += 0.006 * (1 - Math.pow(p[0] / BED.hl, 2)) * (1 - Math.pow(p[2] / BED.hw, 2));
      return p;
    });
    /* two pillows, domed in the middle and thin at the edges */
    [-1, 1].forEach(function (s) {
      var cz = s * PILLOW.z;
      roundedBox(m, [PILLOW.x, BED.T + PILLOW.hy * 0.55, cz], [PILLOW.hx, PILLOW.hy, PILLOW.hz], 0.05, function () { return 3; }, function (p) {
        var fx = 1 - Math.pow(Math.abs(p[0]) / PILLOW.hx, 2.0), fz = 1 - Math.pow(Math.abs(p[2]) / PILLOW.hz, 2.6);
        var f = 0.1 + 0.9 * Math.pow(Math.max(0, fx * fz), 0.62);
        p[1] *= f;
        /* the corners of a pillow pinch in a little, and the middle sags where the filling settles */
        var cz = Math.abs(p[2]) / PILLOW.hz, cx = Math.abs(p[0]) / PILLOW.hx;
        p[0] *= 1 - 0.08 * Math.pow(cz, 6);
        p[2] *= 1 - 0.05 * Math.pow(cx, 4);
        if (p[1] > 0) p[1] -= 0.006 * Math.exp(-Math.pow(p[2] / (PILLOW.hz * 0.45), 2) - Math.pow(p[0] / (PILLOW.hx * 0.5), 2));
        p[1] += (PILLOW.hx - p[0]) * 0.035;
        return p;
      });
    });
    var pos = new Float32Array(m.pos), idx = new Uint16Array(m.idx);
    return { pos: pos, nrm: normals(pos, idx, null, true), mat: new Float32Array(m.mat), idx: idx };
  }

  /* ---------- the sheet: a cloth simulation ---------- */
  function Cloth() {
    var NU = Math.round((SHEET.x1 - SHEET.x0) / SHEET.step) + 1;
    var NV = Math.round((2 * SHEET.zh) / SHEET.step) + 1;
    var N = NU * NV, du = (SHEET.x1 - SHEET.x0) / (NU - 1), dv = 2 * SHEET.zh / (NV - 1);
    var P = new Float32Array(N * 3), Q = new Float32Array(N * 3), UV = new Float32Array(N * 2), flap = new Uint8Array(N);
    var cons = [], i, j, k;
    function id(u, v) { return v * NU + u; }
    for (j = 0; j < NV; j++) for (i = 0; i < NU; i++) { k = id(i, j); UV[k * 2] = SHEET.x0 + i * du; UV[k * 2 + 1] = -SHEET.zh + j * dv; }
    function link(a, b, stiff) {
      var ax = UV[a * 2] - UV[b * 2], az = UV[a * 2 + 1] - UV[b * 2 + 1];
      cons.push(a, b, Math.sqrt(ax * ax + az * az), stiff);
    }
    for (j = 0; j < NV; j++) for (i = 0; i < NU; i++) {
      k = id(i, j);
      if (i < NU - 1) link(k, id(i + 1, j), 1);
      if (j < NV - 1) link(k, id(i, j + 1), 1);
      if (i < NU - 1 && j < NV - 1) { link(k, id(i + 1, j + 1), 0.7); link(id(i + 1, j), id(i, j + 1), 0.7); }
      if (i < NU - 2) link(k, id(i + 2, j), 0.06);
      if (j < NV - 2) link(k, id(i, j + 2), 0.06);
    }
    /* structural and shear links first, the weak bending links after them; the solver runs the
       bending links on every other pass, which keeps the cost down where it matters least */
    var stiff = [], bend = [];
    for (i = 0; i < cons.length; i += 4) (cons[i + 3] < 0.5 ? bend : stiff).push(cons[i], cons[i + 1], cons[i + 2], cons[i + 3]);
    var C = new Float32Array(stiff.concat(bend)), NSTIFF = stiff.length;
    /* triangles, for drawing */
    var tri = [];
    for (j = 0; j < NV - 1; j++) for (i = 0; i < NU - 1; i++) {
      k = id(i, j);
      if ((i + j) % 2) tri.push(k, k + 1, k + NU + 1, k, k + NU + 1, k + NU);
      else tri.push(k, k + 1, k + NU, k + 1, k + NU + 1, k + NU);
    }
    var IDX = new Uint16Array(tri), NRM = new Float32Array(N * 3);
    return { NU: NU, NV: NV, N: N, P: P, Q: Q, UV: UV, C: C, NSTIFF: NSTIFF, IDX: IDX, NRM: NRM, flap: flap, id: id, du: du, dv: dv };
  }

  /* the fold that turns the corner down: everything on the corner's side of a line from the head
     edge to the near edge is mirrored across it and laid on top */
  var FOLD = { ax: SHEET.x0, az: -0.06, bx: SHEET.x0 + 0.96, bz: SHEET.zh };

  /* signed distance to a rounded box (the crown and body as one), used for collisions */
  function sdBox(px, py, pz, cx, cy, cz, hx, hy, hz, r) {
    var qx = Math.abs(px - cx) - hx + r, qy = Math.abs(py - cy) - hy + r, qz = Math.abs(pz - cz) - hz + r;
    var ox = Math.max(qx, 0), oy = Math.max(qy, 0), oz = Math.max(qz, 0);
    return Math.sqrt(ox * ox + oy * oy + oz * oz) + Math.min(Math.max(qx, Math.max(qy, qz)), 0) - r;
  }
  var SD = { d: 0, nx: 0, ny: 0, nz: 0 };
  function sceneSD(x, y, z) {
    /* the bed as one rounded box, the crown's top edge being the softer one */
    var d = sdBox(x, y, z, 0, BED.T / 2, 0, BED.hl, BED.T / 2, BED.hw, 0.035);
    var best = d, e = 1e-3, s;
    /* pillows: rounded boxes, a bit smaller than drawn so the sheet's head edge tucks under them */
    for (s = -1; s <= 1; s += 2) {
      var dp = sdBox(x, y, z, PILLOW.x, BED.T + PILLOW.hy * 0.5, s * PILLOW.z, PILLOW.hx, PILLOW.hy * 0.62, PILLOW.hz, 0.05);
      if (dp < best) best = dp;
    }
    SD.d = best;
    return best;
  }
  function sdNormal(x, y, z) {
    var e = 1.5e-3, d0 = sceneSD(x, y, z);
    var nx = sceneSD(x + e, y, z) - d0, ny = sceneSD(x, y + e, z) - d0, nz = sceneSD(x, y, z + e) - d0;
    var l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    SD.d = d0; SD.nx = nx / l; SD.ny = ny / l; SD.nz = nz / l;
  }

  function Sim(cloth) {
    var NU = cloth.NU, NV = cloth.NV, N = cloth.N, P = cloth.P, Q = cloth.Q, UV = cloth.UV, C = cloth.C, NSTIFF = cloth.NSTIFF, flap = cloth.flap;
    var TH = SHEET.th, LAYER = 2.2 * TH, HR = SHEET.step * 0.74;
    var grab = null;           /* { k: [indices], w: [weights], o: [[dx,dz] offsets], target: [x,y,z] } */
    var settle = 0;

    /* the made bed with the corner turned down, built by hand; the simulation relaxes it. The fold
       is laid as a soft roll: points just past the fold line go up and over a small cylinder,
       the rest of the corner lies flat on top, upside down. */
    function layout(turned, out) {
      var fx = FOLD.bx - FOLD.ax, fz = FOLD.bz - FOLD.az, fl = Math.sqrt(fx * fx + fz * fz);
      var nx = -fz / fl, nz = fx / fl, k, R = 0.02, ARC = Math.PI * R;
      for (k = 0; k < N; k++) {
        var x = UV[k * 2], z = UV[k * 2 + 1], y = BED.T + TH, f = 0;
        if (turned) {
          var s = (x - FOLD.ax) * nx + (z - FOLD.az) * nz;
          if (s > 0) {
            var bx = x - s * nx, bz = z - s * nz, off;
            f = 1;
            if (s < ARC) { var th = s / R; off = R * Math.sin(th); y = BED.T + TH + R * (1 - Math.cos(th)); }
            else { off = -(s - ARC); y = BED.T + TH + 2 * R; }
            x = bx + nx * off; z = bz + nz * off;
          }
        }
        if (!f) {
          var ez = Math.abs(z) - BED.hw, ex = x - BED.hl;
          if (ez > 0) { y -= ez; z = (z < 0 ? -1 : 1) * (BED.hw + TH); }
          if (ex > 0) { y -= ex; x = BED.hl + TH; }
        }
        if (out) { out[k * 3] = x; out[k * 3 + 1] = y; out[k * 3 + 2] = z; continue; }
        flap[k] = f;
        P[k * 3] = x; P[k * 3 + 1] = y; P[k * 3 + 2] = z;
        Q[k * 3] = x; Q[k * 3 + 1] = y; Q[k * 3 + 2] = z;
      }
    }

    /* steering: the sheet is drawn toward a laid-out shape, one point after another, starting
       at the corner a hand is holding; only points that end up lying on the bed are steered,
       the rest hang under gravity. weights: a Float32Array set every frame by the caller. */
    var steerT = null, steerW = null;
    function steer(T, W) { steerT = T; steerW = W; }
    function steerPass() {
      if (!steerT) return;
      for (var k = 0; k < N; k++) {
        var w = steerW[k];
        if (!w) continue;
        var o = k * 3;
        P[o] += (steerT[o] - P[o]) * w; P[o + 1] += (steerT[o + 1] - P[o + 1]) * w; P[o + 2] += (steerT[o + 2] - P[o + 2]) * w;
      }
    }

    /* the 2D hash for the two-layer contact */
    var HC = SHEET.step, HX0 = -1.4, HZ0 = -1.1, HNX = Math.ceil(2.8 / HC), HNZ = Math.ceil(2.2 / HC);
    var head = new Int32Array(HNX * HNZ), next = new Int32Array(N);
    function hashBuild() {
      head.fill(-1);
      for (var k = 0; k < N; k++) {
        var cx = Math.floor((P[k * 3] - HX0) / HC), cz = Math.floor((P[k * 3 + 2] - HZ0) / HC);
        if (cx < 0 || cz < 0 || cx >= HNX || cz >= HNZ) { next[k] = -1; continue; }
        var c = cz * HNX + cx; next[k] = head[c]; head[c] = k;
      }
    }
    /* Two layers of sheet keep a sheet's thickness between them: any two points that are not
       close neighbours in the weave, that sit over one another (within a disc in plan) and closer
       than that thickness, are pushed apart in height. Points that hang down the sides are a
       weave row apart and never trigger it. */
    function layers() {
      hashBuild();
      var k, x, y, z, cx, cz, a, b, c, q, ku, kv, qu, qv, dx, dz, dy, push;
      var ON = BED.T - 0.015;
      for (k = 0; k < N; k++) {
        x = P[k * 3]; y = P[k * 3 + 1]; z = P[k * 3 + 2];
        /* only sheet lying on the bed is stacked; where it hangs down the sides the folds stand
           upright and must not be pushed up or down */
        if (y < ON) continue;
        cx = Math.floor((x - HX0) / HC); cz = Math.floor((z - HZ0) / HC);
        ku = k % NU; kv = (k / NU) | 0;
        for (b = cz - 1; b <= cz + 1; b++) {
          if (b < 0 || b >= HNZ) continue;
          for (a = cx - 1; a <= cx + 1; a++) {
            if (a < 0 || a >= HNX) continue;
            for (q = head[b * HNX + a]; q !== -1; q = next[q]) {
              if (q <= k) continue;
              qu = q % NU; qv = (q / NU) | 0;
              if (Math.abs(qu - ku) <= 2 && Math.abs(qv - kv) <= 2) continue;
              if (P[q * 3 + 1] < ON) continue;
              dx = P[q * 3] - x; dz = P[q * 3 + 2] - z;
              if (dx * dx + dz * dz > HR * HR) continue;
              dy = P[q * 3 + 1] - P[k * 3 + 1];
              if (Math.abs(dy) >= LAYER) continue;
              push = (LAYER - Math.abs(dy)) * 0.5 * (dy >= 0 ? 1 : -1);
              P[q * 3 + 1] += push; P[k * 3 + 1] -= push;
              /* contact holds: a sheet lying on sheet does not slide */
              Q[q * 3] += (P[q * 3] - Q[q * 3]) * 0.35; Q[q * 3 + 2] += (P[q * 3 + 2] - Q[q * 3 + 2]) * 0.35;
              Q[k * 3] += (P[k * 3] - Q[k * 3]) * 0.35; Q[k * 3 + 2] += (P[k * 3 + 2] - Q[k * 3 + 2]) * 0.35;
            }
          }
        }
      }
    }

    function collide() {
      for (var k = 0; k < N; k++) {
        var o = k * 3, x = P[o], y = P[o + 1], z = P[o + 2];
        if (y < TH) { P[o + 1] = TH; Q[o] += (x - Q[o]) * 0.6; Q[o + 2] += (z - Q[o + 2]) * 0.6; }
        /* cheap reject: well away from the bed */
        if (y > BED.T + 0.3 || Math.abs(x) > BED.hl + 0.08 || Math.abs(z) > BED.hw + 0.08) continue;
        if (sceneSD(x, y, z) < TH) {
          sdNormal(x, y, z);
          var pen = TH - SD.d;
          P[o] += SD.nx * pen; P[o + 1] += SD.ny * pen; P[o + 2] += SD.nz * pen;
          /* friction: cotton on a quilted top barely slides; on the sides it slides more */
          var fr = SD.ny > 0.6 ? 0.82 : 0.35;
          Q[o] += (P[o] - Q[o]) * fr; Q[o + 2] += (P[o + 2] - Q[o + 2]) * fr;
          if (SD.ny < 0.6) Q[o + 1] += (P[o + 1] - Q[o + 1]) * 0.2;
        }
      }
    }

    function satisfy(iter) {
      var c, a, b, dx, dy, dz, d, diff, s, n;
      for (var it = 0; it < iter; it++) {
        n = it % 2 ? NSTIFF : C.length;
        for (c = 0; c < n; c += 4) {
          a = C[c] * 3; b = C[c + 1] * 3;
          dx = P[b] - P[a]; dy = P[b + 1] - P[a + 1]; dz = P[b + 2] - P[a + 2];
          d = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (d < 1e-9) continue;
          diff = (d - C[c + 2]) / d;
          /* cloth stretches very little but crumples freely: shorter-than-rest only for the
             structural links */
          if (diff < 0 && C[c + 3] < 1) diff *= 0.5;
          s = diff * 0.5 * C[c + 3];
          P[a] += dx * s; P[a + 1] += dy * s; P[a + 2] += dz * s;
          P[b] -= dx * s; P[b + 1] -= dy * s; P[b + 2] -= dz * s;
        }
        if (grab) pull();
        if (steerT && it % 2 === 0) steerPass();
      }
    }

    function pull() {
      for (var i = 0; i < grab.k.length; i++) {
        var o = grab.k[i] * 3, w = grab.w[i], t = grab.target;
        P[o] += (t[0] + grab.o[i][0] - P[o]) * w;
        P[o + 1] += (t[1] + grab.o[i][1] - P[o + 1]) * w;
        P[o + 2] += (t[2] + grab.o[i][2] - P[o + 2]) * w;
      }
    }

    var DAMP = 0.992, vmaxK = -1;
    function damp(d) { DAMP = d; }
    function rest() { Q.set(P); }
    function integrate(dt) {
      var g = -G * dt * dt, vmax = 0;
      for (var k = 0; k < N; k++) {
        var o = k * 3;
        var vx = (P[o] - Q[o]) * DAMP, vy = (P[o + 1] - Q[o + 1]) * DAMP, vz = (P[o + 2] - Q[o + 2]) * DAMP;
        var v2 = vx * vx + vy * vy + vz * vz;
        if (v2 > vmax) { vmax = v2; vmaxK = k; }
        Q[o] = P[o]; Q[o + 1] = P[o + 1]; Q[o + 2] = P[o + 2];
        P[o] += vx; P[o + 1] += vy + g; P[o + 2] += vz;
      }
      return Math.sqrt(vmax) / dt;
    }

    function step(dt, iter) {
      var v = integrate(dt);
      satisfy(iter || 10);
      collide();
      layers();
      collide();
      return v;
    }

    function timed(dt, iter) {
      var t = [performance.now()];
      integrate(dt); t.push(performance.now());
      satisfy(iter || 10); t.push(performance.now());
      collide(); t.push(performance.now());
      layers(); t.push(performance.now());
      collide(); t.push(performance.now());
      return t.map(function (x, i) { return i ? x - t[i - 1] : 0; }).slice(1);
    }
    return {
      layout: layout, step: step, timed: timed, damp: damp, rest: rest, steer: steer, hot: function () { return [vmaxK % NU, (vmaxK / NU) | 0, P[vmaxK * 3], P[vmaxK * 3 + 1], P[vmaxK * 3 + 2], flap[vmaxK]]; },
      grab: function (g) { grab = g; },
      grabbed: function () { return grab; }
    };
  }

  /* ---------- shaders ---------- */
  var HEAD = 'precision highp float;';
  var LIGHT = [
    'uniform vec3 uEye, uKey, uSky, uGround, uLightDir; uniform float uExpo;',
    'uniform sampler2D uHM; uniform vec4 uHMBox; uniform float uDark;',
    /* occlusion from whatever lies above this point (the sheet, a fold, the pillows), read from a',
       top-down height map and taken a little toward the light, so a fold throws a soft shadow */
    'float hm(vec2 p){ vec4 t = texture2D(uHM, (p - uHMBox.xy) * uHMBox.zw); return (t.r + t.g / 255.0) * 0.8; }',
    'float occl(vec3 p){',
    '  vec2 l = normalize(uLightDir.xz) * 0.03;',
    '  float s = 0.0;',
    '  for (int i = 0; i < 6; i++) {',
    '    float fi = float(i);',
    '    vec2 q = p.xz + l * fi + vec2(cos(fi * 2.4), sin(fi * 2.4)) * 0.01 * fi;',
    '    float h = hm(q) - p.y - 0.003 - fi * 0.005;',
    '    s += clamp(h / 0.03, 0.0, 1.0) * (1.0 - fi * 0.1);',
    '  }',
    '  return s / 4.5;',
    '}',
    /* a filmic curve (Narkowicz's fit of ACES), so white cotton keeps its shading instead of
       clipping to flat white */
    'vec3 film(vec3 x){ return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0); }',
    'vec3 shade(vec3 base, vec3 N, vec3 P, float wrap, float sheen, float spec, float ao){',
    '  vec3 V = normalize(uEye - P), L = normalize(uLightDir);',
    '  float d = clamp((dot(N, L) + wrap) / (1.0 + wrap), 0.0, 1.0);',
    '  vec3 amb = mix(uGround, uSky, N.y * 0.5 + 0.5);',
    '  vec3 c = base * (amb * (0.55 + 0.45 * ao) + uKey * d * ao);',
    '  float f = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 3.0);',
    '  c += sheen * f * mix(vec3(1.0), uSky, 0.3) * ao;',
    '  vec3 H = normalize(L + V);',
    '  c += spec * pow(clamp(dot(N, H), 0.0, 1.0), 40.0) * uKey * ao;',
    '  return film(c * uExpo);',
    '}'
  ].join('\n');

  var VS_SOLID = [HEAD,
    'attribute vec3 aPos, aNrm; attribute float aMat;',
    'uniform mat4 uVP; uniform vec3 uFit;',
    'varying vec3 vP, vN; varying float vM;',
    'void main(){ vP = aPos; vN = aNrm; vM = aMat; vec4 c = uVP * vec4(aPos, 1.0);',
    '  c.xy = c.xy * uFit.x + uFit.yz * c.w; gl_Position = c; }'].join('\n');
  var FS_SOLID = [HEAD, LIGHT,
    'uniform vec3 cQuilt, cStitch, cCrown, cBorder, cRing, cRing2, cBase, cPillow, cPipe;',
    'uniform vec3 uBed;',  /* hl, hw, T */
    'varying vec3 vP, vN; varying float vM;',
    /* a pillow-top quilt: diamonds of puffed fabric between stitch lines, with a stitch every
       few millimetres; returns the bump's slope (for the normal) and the stitch mask */
    'vec3 quilt(vec2 p){',
    '  float s = 0.135; vec2 q = vec2(p.x + p.y, p.x - p.y) / s;',
    '  vec2 f = fract(q) - 0.5; vec2 g = abs(f);',
    '  float b = (0.25 - g.x * g.x) * (0.25 - g.y * g.y) * 16.0;',
    '  vec2 dq = vec2(-2.0 * f.x * (0.25 - g.y * g.y), -2.0 * f.y * (0.25 - g.x * g.x)) * 16.0;',
    '  vec2 dp = vec2(dq.x + dq.y, dq.x - dq.y) / s;',
    '  float line = 1.0 - smoothstep(0.0, 0.035, min(0.5 - g.x, 0.5 - g.y));',
    '  float dash = step(0.5, fract((q.x + q.y) * 9.0));',
    '  return vec3(dp * 0.0022, line * (0.55 + 0.45 * dash) * (1.0 - b));',
    '}',
    /* the border: a light-blue ring stitch on deep blue, in rows */
    'float rings(vec2 p){',
    '  vec2 s = vec2(0.05, 0.043); vec2 c = p / s; vec2 i = floor(c); vec2 f = fract(c) - 0.5;',
    '  if (mod(i.y, 2.0) > 0.5) { f.x = fract(c.x + 0.5) - 0.5; }',
    '  float r = length(f * vec2(1.0, s.y / s.x));',
    '  float ring = 1.0 - smoothstep(0.02, 0.055, abs(r - 0.33));',
    '  float dotc = 1.0 - smoothstep(0.02, 0.06, r);',
    /* a fine knit under the stitching */
    '  float knit = 0.5 + 0.5 * sin(p.y * 1400.0);',
    '  return max(ring, dotc * 0.5) + knit * 0.06;',
    '}',
    'void main(){',
    '  vec3 N = normalize(vN), P = vP; float m = floor(vM + 0.5);',
    '  vec3 col; float wrap = 0.3, sheen = 0.05, spec = 0.0;',
    '  float ao = 1.0;',
    '  if (m < 1.5) {',
    /* the crown: a quilted top, and soft sides with a seam where the crown meets the border */
    '    float top = smoothstep(0.45, 0.8, N.y);',
    '    vec3 q = quilt(P.xz);',
    '    vec3 Nt = normalize(N + vec3(-q.x, 0.0, -q.y));',
    '    vec3 ct = mix(cQuilt, cStitch, q.z * 0.85);',
    '    float y = P.y - (uBed.z - 0.1);',
    '    vec3 cs = cCrown * (0.95 + 0.05 * sin(P.y * 700.0));',
    '    cs = mix(cs, cPipe, 1.0 - smoothstep(0.004, 0.011, y));',
    '    col = mix(cs, ct, top); N = normalize(mix(N, Nt, top));',
    '    ao = mix(0.9 + 0.1 * smoothstep(0.0, 0.03, y), 1.0 - 0.62 * occl(P), top);',
    '    sheen = 0.08;',
    '  } else if (m < 2.5) {',
    '    vec2 sp = abs(N.z) > abs(N.x) ? vec2(P.x, P.y) : vec2(P.z, P.y);',
    '    float r = rings(sp);',
    '    col = mix(cBorder, cRing, r * 0.6);',
    /* a plain band at the foot of the border, where the bed meets the base */
    '    col = mix(cBase, col, smoothstep(0.03, 0.036, P.y));',
    /* the top tape: a band of piping right under the crown */
    '    float top = uBed.z - 0.1 + 0.016 - P.y;',
    '    col = mix(cPipe, col, smoothstep(0.012, 0.016, top));',
    '    ao = 0.82 + 0.18 * smoothstep(0.0, 0.06, top);',
    '    spec = 0.05; wrap = 0.2;',
    '  } else if (m < 3.5) {',
    '    col = cPillow; sheen = 0.12; wrap = 0.45;',
    /* the pillow's corded edge, round its middle */
    '    float e = abs(P.y - (uBed.z + 0.042));',
    '    col *= 1.0 - 0.06 * (1.0 - smoothstep(0.002, 0.006, e)) * smoothstep(0.5, 0.2, abs(N.y));',
    '    ao = 1.0 - 0.35 * occl(P + vec3(0.0, 0.03, 0.0));',
    '  } else { col = cBase; }',
    '  vec3 c = shade(col, N, P, wrap, sheen, spec, ao);',
    '  gl_FragColor = vec4(c, 1.0);',
    '}'].join('\n');

  var VS_CLOTH = [HEAD,
    'attribute vec3 aPos, aNrm; attribute vec2 aUV;',
    'uniform mat4 uVP; uniform vec3 uFit;',
    'varying vec3 vP, vN; varying vec2 vUV;',
    'void main(){ vP = aPos; vN = aNrm; vUV = aUV; vec4 c = uVP * vec4(aPos, 1.0);',
    '  c.xy = c.xy * uFit.x + uFit.yz * c.w; gl_Position = c; }'].join('\n');
  var FS_CLOTH = [HEAD, LIGHT,
    'uniform vec3 cSheet, cSheetBack, cHem, cHemLine; uniform vec2 uHem;',
    'varying vec3 vP, vN; varying vec2 vUV;',
    'void main(){',
    '  vec3 N = normalize(vN);',
    '  vec3 base = cSheet;',
    '  if (!gl_FrontFacing) { N = -N; }',
    /* the inside of the sheet is a touch greyer, so a fold reads */
    '  if (dot(N, vec3(0.0, 1.0, 0.0)) < -2.0) base = cSheetBack;',
    '  if (!gl_FrontFacing) base = cSheetBack;',
    /* the hem: a wide band along the head edge with two stitch lines, and a thin blue piping */
    '  float e = vUV.x - uHem.x;',
    '  float band = 1.0 - smoothstep(0.108, 0.111, e);',
    '  base = mix(base, cHem, band);',
    '  float st = (1.0 - smoothstep(0.0012, 0.0026, abs(e - 0.104))) * step(0.45, fract(vUV.y * 110.0));',
    '  base = mix(base, cHemLine, st * 0.6);',
    '  base = mix(base, cHemLine, (1.0 - smoothstep(0.0035, 0.0055, abs(e - 0.012))) * 0.9);',
    /* percale weave, very fine: a slight cross-hatch in the light */
    '  float w = sin(vUV.x * 1400.0) * sin(vUV.y * 1400.0);',
    '  N = normalize(N + vec3(w * 0.02, 0.0, -w * 0.02));',
    /* soft, long creases a sheet keeps from the fold in the linen closet */
    '  vec2 u = vUV * vec2(5.3, 3.1);',
    '  float cr = sin(u.x * 3.1 + sin(u.y * 2.3) * 1.7) * 0.6 + sin(u.y * 4.7 + sin(u.x * 1.9) * 2.1) * 0.4;',
    '  vec2 g = vec2(cos(u.x * 3.1 + sin(u.y * 2.3) * 1.7) * 3.1 * 5.3 * 0.6, cos(u.y * 4.7 + sin(u.x * 1.9) * 2.1) * 4.7 * 3.1 * 0.4) * 0.0032;',
    '  N = normalize(N + vec3(g.x, 0.0, g.y) * (0.6 + 0.4 * cr));',
    '  float ao = 1.0 - 0.55 * occl(vP);',
    '  vec3 c = shade(base, N, vP, 0.55, 0.16, 0.06, ao);',
    '  gl_FragColor = vec4(c, 1.0);',
    '}'].join('\n');

  var VS_HM = [HEAD,
    'attribute vec3 aPos; uniform vec4 uHMBox; varying float vY;',
    'void main(){ vY = aPos.y; vec2 t = (aPos.xz - uHMBox.xy) * uHMBox.zw * 2.0 - 1.0;',
    '  gl_Position = vec4(t.x, t.y, 1.0 - aPos.y, 1.0); }'].join('\n');
  var FS_HM = [HEAD, 'varying float vY;',
    'void main(){ float h = clamp(vY / 0.8, 0.0, 0.9999); float hi = floor(h * 255.0) / 255.0;',
    '  gl_FragColor = vec4(hi, fract(h * 255.0), 0.0, 1.0); }'].join('\n');

  var VS_FLOOR = [HEAD,
    'attribute vec2 aXZ; uniform mat4 uVP; uniform vec3 uFit; varying vec2 vXZ;',
    'void main(){ vXZ = aXZ; vec4 c = uVP * vec4(aXZ.x, 0.0, aXZ.y, 1.0);',
    '  c.xy = c.xy * uFit.x + uFit.yz * c.w; gl_Position = c; }'].join('\n');
  var FS_FLOOR = [HEAD,
    'uniform vec3 uBed; uniform vec3 cShadow; uniform float uShadow; varying vec2 vXZ;',
    'void main(){',
    '  vec2 q = abs(vXZ - vec2(0.03, -0.03)) - uBed.xy + 0.05;',
    '  float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - 0.05;',
    '  float a = (1.0 - smoothstep(-0.04, 0.32, d)) * 0.55 + (1.0 - smoothstep(-0.01, 0.05, d)) * 0.45;',
    '  gl_FragColor = vec4(cShadow * a * uShadow, a * uShadow);',
    '}'].join('\n');

  /* ---------- the hero ---------- */
  heroes.turndown = function (root) {
    var hero = root.querySelector('[data-mo-lineup-hero]');
    var stage = hero && hero.querySelector('.mo-lineup__stage');
    var canvas = stage && stage.querySelector('canvas');
    if (!canvas) return function () {};
    /* reduced motion: the still under the canvas is this same scene; nothing runs */
    if (still && !window.__moTdForce) { stage.classList.add('is-still'); return function () {}; }
    var gl = null;
    try {
      gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: true, preserveDrawingBuffer: !!window.__moTdKeep }) ||
        canvas.getContext('experimental-webgl');
    } catch (e) { gl = null; }
    if (!gl) { stage.classList.add('is-flat'); return function () {}; }

    var bed = buildBed(), cloth = Cloth(), sim = Sim(cloth);
    /* The sheet is drawn at twice the simulation's resolution: each frame the simulated points are
       refined with the four-point scheme (Dyn, Levin and Gregory), across then down. The refined
       surface passes through every simulated point and has no corners between them, so folds and
       hanging edges stay round without paying for a finer simulation. */
    var FU = cloth.NU * 2 - 1, FV = cloth.NV * 2 - 1, FN = FU * FV;
    var FP = new Float32Array(FN * 3), FNRM = new Float32Array(FN * 3), FUV = new Float32Array(FN * 2), ROW = new Float32Array(FU * 3 * cloth.NV);
    var FIDX = (function () {
      var t = [], i, j, k;
      for (j = 0; j < FV - 1; j++) for (i = 0; i < FU - 1; i++) {
        k = j * FU + i;
        if ((i + j) % 2) t.push(k, k + 1, k + FU + 1, k, k + FU + 1, k + FU);
        else t.push(k, k + 1, k + FU, k + 1, k + FU + 1, k + FU);
      }
      return new Uint16Array(t);
    })();
    (function () {
      for (var j = 0; j < FV; j++) for (var i = 0; i < FU; i++) {
        var k = j * FU + i;
        FUV[k * 2] = SHEET.x0 + i * cloth.du / 2; FUV[k * 2 + 1] = -SHEET.zh + j * cloth.dv / 2;
      }
    })();
    function refine() {
      var P = cloth.P, NU = cloth.NU, NV = cloth.NV, i, j, c, a, b, p0, p3, o;
      /* across: every simulated row becomes a row of FU points */
      for (j = 0; j < NV; j++) {
        for (i = 0; i < NU; i++) { a = (j * NU + i) * 3; o = (j * FU + i * 2) * 3; ROW[o] = P[a]; ROW[o + 1] = P[a + 1]; ROW[o + 2] = P[a + 2]; }
        for (i = 0; i < NU - 1; i++) {
          a = (j * NU + i) * 3; b = a + 3;
          p0 = i > 0 ? a - 3 : a; p3 = i < NU - 2 ? b + 3 : b;
          o = (j * FU + i * 2 + 1) * 3;
          for (c = 0; c < 3; c++) ROW[o + c] = 0.5625 * (P[a + c] + P[b + c]) - 0.0625 * (P[p0 + c] + P[p3 + c]);
        }
      }
      /* down: every column of those rows becomes FV points */
      for (i = 0; i < FU; i++) {
        for (j = 0; j < NV; j++) { a = (j * FU + i) * 3; o = (j * 2 * FU + i) * 3; FP[o] = ROW[a]; FP[o + 1] = ROW[a + 1]; FP[o + 2] = ROW[a + 2]; }
        for (j = 0; j < NV - 1; j++) {
          a = (j * FU + i) * 3; b = a + FU * 3;
          p0 = j > 0 ? a - FU * 3 : a; p3 = j < NV - 2 ? b + FU * 3 : b;
          o = ((j * 2 + 1) * FU + i) * 3;
          for (c = 0; c < 3; c++) FP[o + c] = 0.5625 * (ROW[a + c] + ROW[b + c]) - 0.0625 * (ROW[p0 + c] + ROW[p3 + c]);
        }
      }
    }
    var progs = {}, bufs = {}, fbo = null, hmTex = null, HMW = 256, HMH = 200;
    var HMBOX = [-1.35, -1.1, 1 / 2.7, 1 / 2.2];
    var floorXZ = new Float32Array([-1.9, -1.6, 1.9, -1.6, -1.9, 1.6, 1.9, 1.6]);

    function shader(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) + '\n' + src.split('\n').map(function (l, i) { return (i + 1) + ': ' + l; }).join('\n'));
      return s;
    }
    function program(vs, fs) {
      var p = gl.createProgram();
      gl.attachShader(p, shader(gl.VERTEX_SHADER, vs));
      gl.attachShader(p, shader(gl.FRAGMENT_SHADER, fs));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
      var info = { p: p, a: {}, u: {} }, n, i;
      n = gl.getProgramParameter(p, gl.ACTIVE_ATTRIBUTES);
      for (i = 0; i < n; i++) { var a = gl.getActiveAttrib(p, i); info.a[a.name] = gl.getAttribLocation(p, a.name); }
      n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
      for (i = 0; i < n; i++) { var u = gl.getActiveUniform(p, i); info.u[u.name.replace(/\[0\]$/, '')] = gl.getUniformLocation(p, u.name); }
      return info;
    }
    function buffer(data, target, usage) {
      var b = gl.createBuffer();
      gl.bindBuffer(target || gl.ARRAY_BUFFER, b);
      gl.bufferData(target || gl.ARRAY_BUFFER, data, usage || gl.STATIC_DRAW);
      return b;
    }
    function setup() {
      try {
        progs.solid = program(VS_SOLID, FS_SOLID);
        progs.cloth = program(VS_CLOTH, FS_CLOTH);
        progs.hm = program(VS_HM, FS_HM);
        progs.floor = program(VS_FLOOR, FS_FLOOR);
      } catch (err) { if (window.__moTdDebug) console.error(err); return false; }
      bufs.bedPos = buffer(bed.pos); bufs.bedNrm = buffer(bed.nrm); bufs.bedMat = buffer(bed.mat);
      bufs.bedIdx = buffer(bed.idx, gl.ELEMENT_ARRAY_BUFFER);
      bufs.cPos = buffer(FP, gl.ARRAY_BUFFER, gl.DYNAMIC_DRAW);
      bufs.cNrm = buffer(FNRM, gl.ARRAY_BUFFER, gl.DYNAMIC_DRAW);
      bufs.cUV = buffer(FUV);
      bufs.cIdx = buffer(FIDX, gl.ELEMENT_ARRAY_BUFFER);
      bufs.floor = buffer(floorXZ);
      hmTex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, hmTex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, HMW, HMH, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      var rb = gl.createRenderbuffer();
      gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
      gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, HMW, HMH);
      fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, hmTex, 0);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, rb);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return true;
    }
    if (!setup()) { stage.classList.add('is-flat'); return function () {}; }

    var COL = {};
    function readColors() {
      var css = getComputedStyle(stage);
      function c(name, fb) { return rgb(css.getPropertyValue(name), fb); }
      COL.quilt = c('--sr-quilt', [0.95, 0.945, 0.93]); COL.stitch = c('--sr-stitch', [0.78, 0.8, 0.82]);
      COL.crown = c('--sr-crown', [0.93, 0.93, 0.92]); COL.border = c('--sr-border', [0.13, 0.2, 0.3]);
      COL.ring = c('--sr-ring', [0.55, 0.7, 0.85]); COL.base = c('--sr-base', [0.2, 0.22, 0.25]);
      COL.pillow = c('--sr-pillow', [0.97, 0.965, 0.955]); COL.pipe = c('--sr-pipe', [0.16, 0.24, 0.36]);
      COL.sheet = c('--sr-sheet', [0.985, 0.98, 0.97]); COL.sheetBack = c('--sr-sheet-back', [0.93, 0.93, 0.925]);
      COL.hem = c('--sr-hem', [0.965, 0.96, 0.95]); COL.hemLine = c('--sr-hem-line', [0.16, 0.26, 0.42]);
      COL.key = c('--sr-key', [0.78, 0.76, 0.72]); COL.sky = c('--sr-sky', [0.36, 0.37, 0.4]); COL.ground = c('--sr-ground', [0.24, 0.22, 0.2]);
      COL.shadow = c('--sr-shadow-color', [0.16, 0.14, 0.12]);
      COL.dark = document.documentElement.getAttribute('data-mo-scheme') === 'dark' ? 1 : 0;
      COL.shadowA = parseFloat(css.getPropertyValue('--sr-shadow')) || 0.32;
      COL.expo = parseFloat(css.getPropertyValue('--sr-expo')) || 1.25;
    }

    /* ---------- camera, fitted to the bed on every screen ---------- */
    var W = 0, H = 0, dpr = 1, VP = null, FIT = [1, 0, 0], EYE = null, CAM = null, FITBY = '';
    var LIGHTDIR = norm([-0.5, 1.0, 0.3]);
    /* The scene is framed for one fixed shape (PA, the still image's shape) and that frame sits
       centred in the stage the way the still does with object-fit: contain. So the live drawing
       lands exactly on top of the still at every screen size, and the hand-over never jumps. */
    var PA = 1.5;
    function camera(aspect) {
      var o = window.__moTdCam || {};
      var target = o.target || [0.02, 0.16, 0.0];
      var dir = norm(o.dir || [0.46, 0.62, 1.0]);
      var dist = o.dist || 4.4;
      var eye = [target[0] + dir[0] * dist, target[1] + dir[1] * dist, target[2] + dir[2] * dist];
      var fov = o.fov || 0.52;
      var view = lookAt(eye, target, [0, 1, 0]), proj = persp(fov, PA, 0.5, 12);
      var vp = mul(proj, view);
      /* fit: the bed, the pillows and the sheet hanging off it, in the frame's device space */
      var x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
      [-1, 1].forEach(function (sx) { [-1, 1].forEach(function (sz) { [0, BED.T + 0.14].forEach(function (yy) {
        var q = xform(vp, [sx * (BED.hl + 0.1), yy, sz * (BED.hw + 0.1)]);
        x0 = Math.min(x0, q[0]); x1 = Math.max(x1, q[0]); y0 = Math.min(y0, q[1]); y1 = Math.max(y1, q[1]);
      }); }); });
      var mL = o.mL != null ? o.mL : 0.03, mR = o.mR != null ? o.mR : 0.03, mT = o.mT != null ? o.mT : 0.04, mB = o.mB != null ? o.mB : 0.08;
      var sxw = (2 - mL - mR) / (x1 - x0), syh = (2 - mT - mB) / (y1 - y0), s = Math.min(sxw, syh);
      FITBY = sxw < syh ? 'width' : 'height';
      var ax = o.ax != null ? o.ax : 0.5, ay = o.ay != null ? o.ay : 0.35;
      var ox = (-1 + mL) - x0 * s + ((2 - mL - mR) - (x1 - x0) * s) * ax;
      var oy = (-1 + mB) - y0 * s + ((2 - mT - mB) - (y1 - y0) * s) * ay;
      /* the frame inside the stage: full height and centred when the stage is wider, full width
         and centred when it is narrower */
      var fw = aspect > PA ? PA / aspect : 1, fh = aspect > PA ? 1 : aspect / PA;
      var box = mul([fw, 0, 0, 0, 0, fh, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1], vp);
      return { vp: box, fit: [s, ox * fw, oy * fh], eye: eye, fw: fw, fh: fh, fov: fov, target: target };
    }
    function layout() {
      var box = canvas.getBoundingClientRect();
      W = box.width; H = box.height;
      if (!W || !H) { VP = null; return; }
      dpr = Math.min(2, Math.max(1.25, window.devicePixelRatio || 1));
      if (W * H > 380000) dpr = Math.min(dpr, 1.6);
      if (window.__moTdDpr) dpr = window.__moTdDpr;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      CAM = camera(W / H);
      VP = CAM.vp; FIT = CAM.fit; EYE = CAM.eye;
    }
    /* a point in the scene to the page, and a page point to a ray */
    function toScreen(p) {
      var q = xform(VP, p);
      var x = q[0] * FIT[0] + FIT[1], y = q[1] * FIT[0] + FIT[2];
      return { x: (x + 1) / 2 * W, y: (1 - y) / 2 * H };
    }
    function rayAt(clientX, clientY) {
      var box = canvas.getBoundingClientRect();
      var nx = (clientX - box.left) / W * 2 - 1, ny = 1 - (clientY - box.top) / H * 2;
      var xf = (nx - FIT[1]) / (FIT[0] * CAM.fw), yf = (ny - FIT[2]) / (FIT[0] * CAM.fh);
      var f = norm(sub(CAM.target, EYE)), s = norm(cross(f, [0, 1, 0])), u = cross(s, f), t = Math.tan(CAM.fov / 2);
      return { o: EYE, d: norm([f[0] + s[0] * xf * t * PA + u[0] * yf * t, f[1] + s[1] * xf * t * PA + u[1] * yf * t, f[2] + s[2] * xf * t * PA + u[2] * yf * t]) };
    }

    /* ---------- drawing ---------- */
    /* one pass of smoothing over the sheet's normals, between neighbours that face the same way
       (so a fold keeps its edge), which keeps the weave's triangles from showing in the light */
    var NTMP = new Float32Array(FN * 3);
    function smoothNormals() {
      var n = FNRM, NU = FU, NV = FV, u, v, du2, dv2, k, q, w, d, sx, sy, sz;
      for (v = 0; v < NV; v++) for (u = 0; u < NU; u++) {
        k = (v * NU + u) * 3; sx = n[k] * 4; sy = n[k + 1] * 4; sz = n[k + 2] * 4;
        for (dv2 = -1; dv2 <= 1; dv2++) for (du2 = -1; du2 <= 1; du2++) {
          if (!du2 && !dv2) continue;
          if (u + du2 < 0 || u + du2 >= NU || v + dv2 < 0 || v + dv2 >= NV) continue;
          q = ((v + dv2) * NU + u + du2) * 3;
          d = n[k] * n[q] + n[k + 1] * n[q + 1] + n[k + 2] * n[q + 2];
          if (d <= 0.2) continue;
          w = (du2 && dv2 ? 1 : 2) * d;
          sx += n[q] * w; sy += n[q + 1] * w; sz += n[q + 2] * w;
        }
        d = Math.sqrt(sx * sx + sy * sy + sz * sz) || 1;
        NTMP[k] = sx / d; NTMP[k + 1] = sy / d; NTMP[k + 2] = sz / d;
      }
      n.set(NTMP);
    }
    function uploadCloth() {
      refine();
      normals(FP, FIDX, FNRM);
      smoothNormals();
      gl.bindBuffer(gl.ARRAY_BUFFER, bufs.cPos); gl.bufferSubData(gl.ARRAY_BUFFER, 0, FP);
      gl.bindBuffer(gl.ARRAY_BUFFER, bufs.cNrm); gl.bufferSubData(gl.ARRAY_BUFFER, 0, FNRM);
    }
    function attr(prog, name, buf, size) {
      var loc = prog.a[name];
      if (loc == null || loc < 0) return;
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
    }
    function off(prog) { Object.keys(prog.a).forEach(function (k) { if (prog.a[k] >= 0) gl.disableVertexAttribArray(prog.a[k]); }); }
    function common(pr) {
      var u = pr.u;
      if (u.uVP) gl.uniformMatrix4fv(u.uVP, false, new Float32Array(VP));
      if (u.uFit) gl.uniform3fv(u.uFit, FIT);
      if (u.uEye) gl.uniform3fv(u.uEye, EYE);
      if (u.uKey) gl.uniform3fv(u.uKey, COL.key);
      if (u.uSky) gl.uniform3fv(u.uSky, COL.sky);
      if (u.uGround) gl.uniform3fv(u.uGround, COL.ground);
      if (u.uLightDir) gl.uniform3fv(u.uLightDir, LIGHTDIR);
      if (u.uHMBox) gl.uniform4fv(u.uHMBox, HMBOX);
      if (u.uDark) gl.uniform1f(u.uDark, COL.dark);
      if (u.uExpo) gl.uniform1f(u.uExpo, COL.expo);
      if (u.uBed) gl.uniform3f(u.uBed, BED.hl, BED.hw, BED.T);
      if (u.uHM) { gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, hmTex); gl.uniform1i(u.uHM, 0); }
    }
    function heightPass() {
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.viewport(0, 0, HMW, HMH);
      gl.clearColor(0, 0, 0, 1); gl.clearDepth(1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LESS); gl.disable(gl.BLEND);
      var pr = progs.hm; gl.useProgram(pr.p);
      gl.uniform4fv(pr.u.uHMBox, HMBOX);
      /* the sheet and the pillows cast; the bed's own top does not */
      attr(pr, 'aPos', bufs.cPos, 3);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bufs.cIdx);
      gl.drawElements(gl.TRIANGLES, FIDX.length, gl.UNSIGNED_SHORT, 0);
      attr(pr, 'aPos', bufs.bedPos, 3);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bufs.bedIdx);
      gl.drawElements(gl.TRIANGLES, bed.pillowCount || 0, gl.UNSIGNED_SHORT, (bed.pillowStart || 0) * 2);
      off(pr);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    var drawn = false, lost = false;
    function draw() {
      if (!VP || lost) return;
      uploadCloth();
      heightPass();
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0); gl.clearDepth(1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      /* the floor shadow, under everything */
      gl.disable(gl.DEPTH_TEST); gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      var pf = progs.floor; gl.useProgram(pf.p); common(pf);
      gl.uniform3fv(pf.u.cShadow, COL.shadow); gl.uniform1f(pf.u.uShadow, COL.shadowA);
      attr(pf, 'aXZ', bufs.floor, 2);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      off(pf);
      gl.disable(gl.BLEND); gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL);
      var ps = progs.solid; gl.useProgram(ps.p); common(ps);
      var U = ps.u;
      gl.uniform3fv(U.cQuilt, COL.quilt); gl.uniform3fv(U.cStitch, COL.stitch); gl.uniform3fv(U.cCrown, COL.crown);
      gl.uniform3fv(U.cBorder, COL.border); gl.uniform3fv(U.cRing, COL.ring); if (U.cRing2) gl.uniform3fv(U.cRing2, COL.ring);
      gl.uniform3fv(U.cBase, COL.base); gl.uniform3fv(U.cPillow, COL.pillow); gl.uniform3fv(U.cPipe, COL.pipe);
      attr(ps, 'aPos', bufs.bedPos, 3); attr(ps, 'aNrm', bufs.bedNrm, 3); attr(ps, 'aMat', bufs.bedMat, 1);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bufs.bedIdx);
      gl.drawElements(gl.TRIANGLES, bed.idx.length, gl.UNSIGNED_SHORT, 0);
      off(ps);
      var pc = progs.cloth; gl.useProgram(pc.p); common(pc);
      gl.enable(gl.POLYGON_OFFSET_FILL); gl.polygonOffset(-2, -2);
      gl.uniform3fv(pc.u.cSheet, COL.sheet); gl.uniform3fv(pc.u.cSheetBack, COL.sheetBack);
      gl.uniform3fv(pc.u.cHem, COL.hem); gl.uniform3fv(pc.u.cHemLine, COL.hemLine);
      gl.uniform2f(pc.u.uHem, SHEET.x0, 0);
      attr(pc, 'aPos', bufs.cPos, 3); attr(pc, 'aNrm', bufs.cNrm, 3); attr(pc, 'aUV', bufs.cUV, 2);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bufs.cIdx);
      gl.drawElements(gl.TRIANGLES, FIDX.length, gl.UNSIGNED_SHORT, 0);
      gl.disable(gl.POLYGON_OFFSET_FILL);
      off(pc);
      if (!drawn) { drawn = true; stage.classList.add('is-live'); if (turnBtn) turnBtn.hidden = false; }
      if (stage.__turndown) stage.__turndown.frames++;
    }

    /* the pillows' slice of the bed's index buffer, for the height pass */
    (function () {
      /* the pillows were added last: find where their material starts */
      var first = -1;
      for (var i = 0; i < bed.idx.length; i++) { if (bed.mat[bed.idx[i]] === 3) { first = i; break; } }
      if (first >= 0) { first -= first % 3; bed.pillowStart = first; bed.pillowCount = bed.idx.length - first; }
    })();

    /* ---------- running the sheet ---------- */
    var raf = 0, last = 0, onScreen = false, quiet = 0, laid = false, ready = false, prerolled = 0, inviteAt = 0;
    var PREROLL = window.__moTdPreroll || 220, SUB = 2, ITER = 10, BUDGET = 10, calm = 0, slow = 0;
    /* the sheet settles before the first frame is shown (the still covers it), a few
       milliseconds per animation frame so the page never stalls */
    function preroll(budget) {
      var t0 = performance.now();
      sim.damp(0.97);
      while (prerolled < PREROLL && (budget == null || performance.now() - t0 < budget)) { sim.step(1 / 120, ITER); prerolled++; }
      if (prerolled >= PREROLL && !ready) { ready = true; draw(); }
      return ready;
    }
    var script = null;   /* a scripted pull: { path: fn(t)->[x,y,z], t0, dur, k } */
    function frame(now) {
      var dt = Math.min(0.033, (now - last) / 1000 || 0.016); last = now;
      if (!VP) { raf = 0; return; }
      if (!ready) { preroll(BUDGET); raf = requestAnimationFrame(frame); return; }
      if (!invited && !window.__moTdNoInvite) { if (!inviteAt) inviteAt = now + 900; else if (now > inviteAt) invite(); }
      if (script) runScript(now);
      /* left alone, the sheet comes to rest: more damping once nobody has touched it for a moment */
      var hands = !!(sim.grabbed() || script);
      calm = hands ? 0 : calm + dt;
      sim.damp(calm > 1.1 ? 0.955 : 0.992);
      var v = 0, sub = SUB, h = 1 / (60 * SUB), t1 = performance.now();
      for (var i = 0; i < sub; i++) v = Math.max(v, sim.step(h, ITER));
      /* a slow device runs one larger step per frame instead of two */
      if (SUB === 2) { slow = (performance.now() - t1) > 10 ? slow + 1 : Math.max(0, slow - 1); if (slow > 12) SUB = 1; }
      draw();
      quiet = v < 0.06 && !hands ? quiet + 1 : 0;
      var waiting = !invited && !window.__moTdNoInvite;
      /* asleep once it is still, or once nobody has touched it for a few seconds */
      var asleep = !waiting && (quiet >= 40 || calm > 3.2);
      if (asleep) sim.rest();
      raf = onScreen && !asleep ? requestAnimationFrame(frame) : 0;
      if (stage.__turndown) stage.__turndown.lastV = v;
    }
    function wake() { if (!raf && onScreen && VP) { quiet = 0; calm = Math.min(calm, 1.2); last = performance.now(); raf = requestAnimationFrame(frame); } }

    /* the first time the bed is on screen, a hand tugs the turned-down corner and lets it fall
       back, so it is plain the sheet can be moved. Once, and only if nobody has touched it. */
    var invited = false, touched = false;
    function invite() {
      if (invited || touched || script || !ready) return;
      invited = true;
      var k = corner(), P = cloth.P, from = [P[k * 3], P[k * 3 + 1], P[k * 3 + 2]];
      var g = grabAt(k, 0);
      g.w = g.w.map(function (w) { return Math.min(1, w * 1.4); });
      script = { invite: true, g: g, t0: performance.now(), dur: 1500, path: function (t) {
        var up = Math.sin(Math.PI * Math.min(1, t * 1.15));
        return [from[0] + 0.05 * up, from[1] + 0.16 * up, from[2] + 0.07 * up];
      } };
      wake();
    }

    /* ---------- the pointer ---------- */
    var active = null;
    function nearestOnSheet(clientX, clientY, maxPx) {
      var box = canvas.getBoundingClientRect(), px = clientX - box.left, py = clientY - box.top;
      var best = -1, bd = maxPx * maxPx, P = cloth.P;
      for (var k = 0; k < cloth.N; k++) {
        var s = toScreen([P[k * 3], P[k * 3 + 1], P[k * 3 + 2]]);
        var dx = s.x - px, dy = s.y - py, d = dx * dx + dy * dy;
        /* prefer the top layer: a point a little higher wins a near tie */
        d -= P[k * 3 + 1] * 900;
        if (d < bd) { bd = d; best = k; }
      }
      return best;
    }
    function grabAt(k, lift) {
      var P = cloth.P, u0 = k % cloth.NU, v0 = (k / cloth.NU) | 0, R = 2, ks = [], ws = [], os = [];
      for (var v = v0 - R; v <= v0 + R; v++) for (var u = u0 - R; u <= u0 + R; u++) {
        if (u < 0 || v < 0 || u >= cloth.NU || v >= cloth.NV) continue;
        var d = Math.sqrt((u - u0) * (u - u0) + (v - v0) * (v - v0)) / (R + 0.6);
        if (d >= 1) continue;
        var q = cloth.id(u, v);
        ks.push(q); ws.push(Math.pow(1 - d, 2) * 0.55);
        os.push([P[q * 3] - P[k * 3], P[q * 3 + 1] - P[k * 3 + 1], P[q * 3 + 2] - P[k * 3 + 2]]);
      }
      var g = { k: ks, w: ws, o: os, target: [P[k * 3], P[k * 3 + 1] + (lift || 0), P[k * 3 + 2]], y: P[k * 3 + 1] + (lift || 0), root: k };
      sim.grab(g);
      return g;
    }
    function planeHit(clientX, clientY, y) {
      var r = rayAt(clientX, clientY);
      if (Math.abs(r.d[1]) < 1e-4) return null;
      var t = (y - r.o[1]) / r.d[1];
      if (t <= 0) return null;
      return [r.o[0] + r.d[0] * t, y, r.o[2] + r.d[2] * t];
    }
    var downAt = null, moved = false;
    function down(e) {
      touched = true;
      if (script && script.invite) { sim.grab(null); script = null; }
      if (script) return;
      var k = nearestOnSheet(e.clientX, e.clientY, e.pointerType === 'touch' ? 46 : 30);
      downAt = { x: e.clientX, y: e.clientY, t: performance.now(), k: k, id: e.pointerId };
      moved = false;
      if (k < 0) return;
      active = { id: e.pointerId, g: null };
      wake();
    }
    function move(e) {
      if (!downAt || e.pointerId !== downAt.id) return;
      var dx = e.clientX - downAt.x, dy = e.clientY - downAt.y;
      if (!moved && dx * dx + dy * dy > 64) {
        moved = true;
        if (active && downAt.k >= 0) {
          active.g = grabAt(downAt.k, 0.07);
          try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* fine */ }
          stage.classList.add('is-pulling');
        }
      }
      if (active && active.g) {
        var hit = planeHit(e.clientX, e.clientY, active.g.y);
        if (hit) {
          hit[0] = clamp(hit[0], -1.6, 1.8); hit[2] = clamp(hit[2], -1.4, 1.4);
          active.g.target = hit;
        }
        wake();
        if (e.cancelable) e.preventDefault();
      }
    }
    function up(e) {
      if (downAt && e && e.pointerId !== downAt.id) return;
      var tapped = downAt && !moved && performance.now() - downAt.t < 450;
      if (active && active.g) sim.grab(null);
      active = null; downAt = null;
      stage.classList.remove('is-pulling');
      if (tapped) toggle();
      wake();
    }

    /* ---------- tap: make the bed, then turn it down again ---------- */
    var turned = true;
    var turnBtn = stage.querySelector('[data-turn]'), turnLabel = turnBtn && turnBtn.querySelector('[data-turn-label]');
    var LABEL_MAKE = turnLabel ? turnLabel.textContent : 'Make the bed';
    function label() { if (turnLabel) turnLabel.textContent = turned ? LABEL_MAKE : 'Turn it down'; }
    if (turnBtn) turnBtn.addEventListener('click', function () { touched = true; if (script && script.invite) { sim.grab(null); script = null; } if (!script) { toggle(); } });
    function corner() { return cloth.id(0, cloth.NV - 1); }
    /* a laid-out target for each state, worked out once */
    var TGT = { up: null, down: null }, SW = new Float32Array(cloth.N), DELAY = new Float32Array(cloth.N);
    (function () {
      var c0 = cloth.id(0, cloth.NV - 1), cu = cloth.UV[c0 * 2], cv = cloth.UV[c0 * 2 + 1], maxd = 0;
      for (var k = 0; k < cloth.N; k++) {
        var du = cloth.UV[k * 2] - cu, dv = cloth.UV[k * 2 + 1] - cv, d = Math.sqrt(du * du + dv * dv);
        DELAY[k] = d; if (d > maxd) maxd = d;
      }
      for (k = 0; k < cloth.N; k++) DELAY[k] = DELAY[k] / maxd;
    })();
    function target(state) {
      if (!TGT[state]) { TGT[state] = new Float32Array(cloth.N * 3); sim.layout(state === 'down', TGT[state]); }
      return TGT[state];
    }
    function runScript(now) {
      var t = clamp((now - script.t0) / script.dur, 0, 1), e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      if (script.g) script.g.target = script.path(e);
      if (script.T) {
        /* each point starts moving a little after the ones nearer the held corner */
        var T = script.T, on = BED.T - 0.02, k, a;
        for (k = 0; k < cloth.N; k++) {
          if (T[k * 3 + 1] < on) { SW[k] = 0; continue; }
          a = clamp((t - DELAY[k] * 0.45) / 0.4, 0, 1);
          SW[k] = a * a * (3 - 2 * a) * 0.09 + (t >= 1 ? 0.1 : 0);
        }
      }
      if (t >= 1) {
        if (!script.hold) script.hold = now;
        if (now - script.hold > 350) { sim.grab(null); sim.steer(null, null); script = null; }
      }
    }
    function toggle() {
      var k = corner(), P = cloth.P;
      var from = [P[k * 3], P[k * 3 + 1], P[k * 3 + 2]];
      var T = target(turned ? 'up' : 'down');
      var to = [T[k * 3], Math.max(T[k * 3 + 1], BED.T + 0.02), T[k * 3 + 2]];
      var peak = turned ? 0.32 : 0.3;
      turned = !turned;
      var g = grabAt(k, 0);
      g.w = g.w.map(function (w) { return Math.min(1, w * 1.6); });
      SW.fill(0);
      sim.steer(T, SW);
      script = { g: g, T: T, t0: performance.now(), dur: 1600, path: function (t) {
        var y = from[1] + (to[1] - from[1]) * t + Math.sin(Math.PI * t) * peak;
        return [from[0] + (to[0] - from[0]) * t, y, from[2] + (to[2] - from[2]) * t];
      } };
      stage.setAttribute('data-turned', turned ? 'down' : 'up');
      label();
      wake();
    }

    function repaint() {
      readColors(); layout();
      if (!VP) return;
      if (!laid) { sim.layout(true); laid = true; }
      if (window.__moTdSync) preroll(null);
      if (ready) draw();
      wake();
    }

    canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); lost = true; if (raf) { cancelAnimationFrame(raf); raf = 0; } stage.classList.remove('is-live'); drawn = false; });
    canvas.addEventListener('webglcontextrestored', function () { lost = false; setup(); repaint(); });
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', function (e) { if (downAt && e.pointerId === downAt.id) { moved = true; up(e); } });
    var begin = function (visible) { onScreen = visible; if (visible) wake(); };
    if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { begin(es[0].isIntersecting); }, { threshold: 0.05 }).observe(canvas);
    else begin(true);
    repaint();
    /* test hooks for QA scripts; harmless in production */
    stage.__turndown = {
      toggle: toggle,
      busy: function () { return !!raf; },
      grab: function (u, v, x, y, z) { var g = grabAt(cloth.id(u, v), 0); g.target = [x, y, z]; wake(); },
      release: function () { sim.grab(null); wake(); },
      screen: function (x, y, z) { return toScreen([x, y, z]); },
      corner: function () { var k = corner(); return [cloth.P[k * 3], cloth.P[k * 3 + 1], cloth.P[k * 3 + 2]]; },
      hot: function () { return sim.hot(); },
      fit: function () { return { by: FITBY, w: W, h: H, a: W / H }; },
      timed: function (n) { var acc = [0, 0, 0, 0, 0]; for (var i = 0; i < n; i++) { var t = sim.timed(1 / 120, ITER); for (var j = 0; j < 5; j++) acc[j] += t[j] / n; } return acc; },
      bench: function (n) { var t = performance.now(); for (var i = 0; i < n; i++) sim.step(1 / 120, ITER); return (performance.now() - t) / n; },
      stats: function () { var lo = 9, hi = -9; for (var k = 0; k < cloth.N; k++) { lo = Math.min(lo, cloth.P[k * 3 + 1]); hi = Math.max(hi, cloth.P[k * 3 + 1]); } return { n: cloth.N, lo: lo, hi: hi, turned: turned }; },
      frames: 0
    };
    return repaint;
  };
  /* ==================================================================================
   * The strip: every model side by side, height to scale (snippets/mo-brand-lineup-skyline).
   * Each bed is a small side view. Under the pointer a coil bed gives a little and springs back
   * past its height before it settles; a memory foam bed sinks at once and comes back slowly.
   * They rise into place the same way the first time the strip is on screen.
   * ================================================================================== */
  var SU = 6;          /* units per inch in the strip drawings */
  var SKY_W = 150, SKY_H = 114, SKY_D = 14;   /* every strip drawing shares one frame, so heights compare */
  function bedSVG(inches, coil, pt, quilt) {
    var W = SKY_W, D = SKY_D, h = Math.max(30, inches * SU), y1 = SKY_H, y0 = y1 - h, top = y0 - D * 0.5;
    var fx0 = 1, fx1 = W - D - 1;
    var o = '<g class="k-bed">';
    /* the top face, seen a little from above */
    o += '<path class="k-top" d="M' + fx0 + ',' + y0 + 'L' + (fx0 + D) + ',' + top + 'H' + (fx1 + D) + 'L' + fx1 + ',' + y0 + 'Z"/>';
    if (quilt || pt) {
      /* quilting on the top face: a few diamonds in perspective */
      for (var qx = fx0 + 10; qx < fx1 - 4; qx += 12) o += '<path class="k-quilt" d="M' + n1(qx) + ',' + n1(y0 - 0.6) + 'L' + n1(qx + D * 0.5 + 4) + ',' + n1(top + D * 0.25) + 'L' + n1(qx + D + 1) + ',' + n1(top + 0.6) + '"/>';
    }
    /* the end of the bed, in shade */
    o += '<path class="k-end" d="M' + fx1 + ',' + y0 + 'L' + (fx1 + D) + ',' + top + 'V' + (y1 - D * 0.5) + 'L' + fx1 + ',' + y1 + 'Z"/>';
    var band = pt ? Math.min(h * 0.24, 3.2 * SU) : quilt ? Math.min(h * 0.16, 2 * SU) : 1.1 * SU;
    if (pt) {
      /* a pillow top: a puffed crown, a stitched seam, then the border */
      o += '<path class="k-crown" d="M' + fx0 + ',' + n1(y0 + band) + 'V' + (y0 + 3) + 'Q' + fx0 + ',' + y0 + ' ' + (fx0 + 3) + ',' + y0 + 'H' + (fx1 - 3) + 'Q' + fx1 + ',' + y0 + ' ' + fx1 + ',' + (y0 + 3) + 'V' + n1(y0 + band) + 'Z"/>';
      o += '<path class="k-crown-end" d="M' + fx1 + ',' + (y0 + 3) + 'L' + (fx1 + D) + ',' + (top + 3) + 'V' + n1(top + band) + 'L' + fx1 + ',' + n1(y0 + band) + 'Z"/>';
      o += '<path class="k-stitch" d="M' + (fx0 + 3) + ',' + n1(y0 + band * 0.5) + 'H' + (fx1 - 3) + '"/>';
    } else {
      o += '<rect class="k-crown" x="' + fx0 + '" y="' + y0 + '" width="' + (fx1 - fx0) + '" height="' + n1(band) + '"/>';
      o += '<path class="k-crown-end" d="M' + fx1 + ',' + y0 + 'L' + (fx1 + D) + ',' + top + 'V' + n1(top + band) + 'L' + fx1 + ',' + n1(y0 + band) + 'Z"/>';
      if (quilt) o += '<path class="k-stitch" d="M' + (fx0 + 3) + ',' + n1(y0 + band * 0.55) + 'H' + (fx1 - 3) + '"/>';
    }
    var py = y0 + band;
    o += '<rect class="k-panel' + (coil ? '' : ' is-foam') + '" x="' + fx0 + '" y="' + n1(py) + '" width="' + (fx1 - fx0) + '" height="' + n1(y1 - py) + '"/>';
    if (coil) {
      /* the light-blue ring stitch on a deep blue border */
      for (var ry = py + 5.5, r = 0; ry < y1 - 3; ry += 7.2, r++)
        for (var rx = fx0 + 5 + (r % 2) * 4.5; rx < fx1 - 3; rx += 9) o += '<circle class="k-ring" cx="' + n1(rx) + '" cy="' + n1(ry) + '" r="2.4"/>';
    } else {
      for (var bx = fx0 + 4; bx < fx1 - 2; bx += 4) o += '<path class="k-rib" d="M' + bx + ',' + n1(py + 1.5) + 'V' + n1(y1 - 1.5) + '"/>';
      o += '<path class="k-stripe" d="M' + fx0 + ',' + n1(py + 2.4) + 'H' + fx1 + '"/>';
    }
    o += '<path class="k-tape" d="M' + fx0 + ',' + n1(py) + 'H' + fx1 + 'M' + fx0 + ',' + n1(y1 - 0.7) + 'H' + fx1 + '"/>';
    o += '<path class="k-end-panel' + (coil ? '' : ' is-foam') + '" d="M' + fx1 + ',' + n1(py) + 'L' + (fx1 + D) + ',' + n1(py - D * 0.5) + 'V' + n1(y1 - D * 0.5) + 'L' + fx1 + ',' + y1 + 'Z"/>';
    o += '</g>';
    return '<svg viewBox="0 0 ' + W + ' ' + SKY_H + '" aria-hidden="true" focusable="false">' + o + '</svg>';
  }

  function skyline(sky, root) {
    var floor = sky.querySelector('.mo-sky__floor');
    var beds = Array.prototype.slice.call(sky.querySelectorAll('[data-sky]'));
    var items = beds.map(function (a, i) {
      var row = document.getElementById(a.getAttribute('data-row-ref'));
      var v = row && (row.querySelector('.mo-lineup__v:not([hidden])') || row.querySelector('.mo-lineup__v'));
      var kinds = v ? Array.prototype.slice.call(v.querySelectorAll('.mo-lineup__layers li')).map(function (li) { return (li.getAttribute('data-kind') || '').trim(); }) : [];
      var inches = parseFloat(a.getAttribute('data-inches')) || 12;
      var coil = a.classList.contains('is-coil');
      var pt = kinds.some(function (k) { return / pt$/.test(k); }), quilt = kinds.indexOf('quilt') !== -1;
      var fig = a.querySelector('[data-sky-fig]');
      if (fig) fig.innerHTML = bedSVG(inches, coil, pt, quilt);
      return { a: a, fig: fig, g: fig && fig.querySelector('.k-bed'), row: row, coil: coil, c: 0, v: 0, target: 0, delay: i * 70 };
    });
    if (!items.length) return null;
    sky.classList.add('is-drawn');

    /* the height lines behind the beds, with their labels at the left of the floor */
    var guides = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    guides.setAttribute('class', 'mo-sky__guides'); guides.setAttribute('aria-hidden', 'true');
    if (floor) floor.insertBefore(guides, floor.firstChild);
    function drawGuides() {
      if (!floor || !items[0].fig) return;
      var fb = floor.getBoundingClientRect(), first = items[0].fig.getBoundingClientRect(), lastFig = items[items.length - 1].fig.getBoundingClientRect();
      if (!fb.width || !first.width) return;
      var scale = first.width / SKY_W, base = first.bottom - fb.top, x0 = first.left - fb.left, x1 = lastFig.right - fb.left;
      guides.setAttribute('width', Math.ceil(fb.width)); guides.setAttribute('height', Math.ceil(fb.height));
      guides.setAttribute('viewBox', '0 0 ' + Math.ceil(fb.width) + ' ' + Math.ceil(fb.height));
      var o = '';
      [8, 12, 16].forEach(function (inch) {
        var y = Math.round(base - inch * SU * scale) + 0.5;
        o += '<path class="mo-sky__guide" d="M' + n1(x0 - 6) + ',' + y + 'H' + n1(x1 + 4) + '"/>';
        o += '<text class="mo-sky__guide-label" x="' + n1(x0 - 10) + '" y="' + (y + 3.5) + '" text-anchor="end">' + inch + ' in.</text>';
      });
      guides.innerHTML = o;
    }
    drawGuides();
    var gt = 0;
    window.addEventListener('resize', function () { clearTimeout(gt); gt = setTimeout(drawGuides, 120); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(drawGuides);

    /* rows a filter hides fade out of the strip too */
    function sync() { items.forEach(function (it) { if (it.row) it.a.classList.toggle('is-out', it.row.hidden); }); }
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(sync);
      items.forEach(function (it) { if (it.row) mo.observe(it.row, { attributes: true, attributeFilter: ['hidden'] }); });
    }
    sync();
    if (still) return items;

    var raf = 0, last = 0, t0 = 0;
    function apply(it) {
      if (!it.g) return;
      var c = it.c, sy = 1 - c, sx = 1 + c * 0.2;
      it.g.style.transform = 'scale(' + sx.toFixed(4) + ',' + sy.toFixed(4) + ')';
    }
    /* fixed small steps, so the springs behave the same on a slow phone as on a fast laptop */
    function tick(it, h) {
      if (it.coil) {
        var a = 300 * (it.target - it.c) - 12 * it.v;
        it.v += a * h; it.c += it.v * h;
      } else {
        var tau = it.target > it.c ? 0.12 : (it.entering ? 0.6 : 1.2);
        it.c += (it.target - it.c) * (1 - Math.exp(-h / tau));
      }
    }
    function step(now) {
      var dt = Math.min(0.1, (now - last) / 1000 || 0.016); last = now;
      var busy = false, n = Math.max(1, Math.round(dt / (1 / 240))), h = dt / n;
      items.forEach(function (it) {
        if (it.entering && now - t0 < it.delay) { busy = true; return; }
        for (var i = 0; i < n; i++) tick(it, h);
        var moving = it.coil ? (Math.abs(it.v) > 0.003 || Math.abs(it.target - it.c) > 0.002) : Math.abs(it.target - it.c) > 0.002;
        if (moving) busy = true;
        else { it.c = it.target; it.v = 0; it.entering = false; }
        apply(it);
      });
      raf = busy ? requestAnimationFrame(step) : 0;
    }
    function wake() { if (!raf) { last = performance.now(); raf = requestAnimationFrame(step); } }
    items.forEach(function (it) {
      var a = it.a;
      a.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { it.target = 0.16; wake(); } });
      a.addEventListener('pointerleave', function () { it.target = 0; wake(); });
      a.addEventListener('pointerdown', function () { it.target = 0.3; wake(); });
      a.addEventListener('pointerup', function (e) { it.target = e.pointerType === 'mouse' ? 0.16 : 0; wake(); });
      a.addEventListener('pointercancel', function () { it.target = 0; wake(); });
      a.addEventListener('focus', function () { it.target = 0.12; wake(); });
      a.addEventListener('blur', function () { it.target = 0; wake(); });
    });
    /* the first time the strip is on screen the beds land, one after another: coils bounce,
       memory foam rises slowly */
    var entered = false;
    function enter() {
      if (entered) return;
      entered = true;
      t0 = performance.now();
      items.forEach(function (it) { it.c = 0.85; it.v = 0; it.target = 0; it.entering = true; apply(it); });
      wake();
    }
    items.forEach(function (it) { it.c = 0.85; apply(it); });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) { if (es[0].isIntersecting) { enter(); io.disconnect(); } }, { threshold: 0.3 });
      io.observe(floor || sky);
    } else enter();
    sky.__sky = { press: function (i, c) { items[i].target = c; wake(); }, state: function () { return items.map(function (it) { return Math.round(it.c * 1000) / 1000; }); }, busy: function () { return !!raf; }, guides: drawGuides };
    return items;
  }

  function bootSkylines(scope) {
    Array.prototype.slice.call((scope || document).querySelectorAll('[data-mo-skyline]')).forEach(function (sky) {
      if (sky.__moSky) return;
      sky.__moSky = true;
      var root = sky.closest('[data-mo-lineup]');
      skyline(sky, root);
    });
  }
  function bootAll(scope) { bootSkylines(scope); }
  /* the strip draws from the rows, so it waits for the page to finish parsing */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { bootAll(document); });
  else bootAll(document);
  document.addEventListener('shopify:section:load', function (e) { bootAll(e.target); });

  /* ==================================================================================
   * Row drawing: a Serta side view with the cover cut back
   * Height is to scale, 5 units per inch, like every lineup drawing. Each layer is drawn at the
   * depth Serta publishes for it (an "in." in the layer name); when the published depths add up
   * to more than the finished height (quilting compresses), all of them are scaled to fit.
   * Kinds: cover, quilt, fiber, aire, wool, gel, twist, fuze, supreme, graphite, deep, comfort,
   * support, balance, zones, core, core-edge, lumbar (a band under the lower back), q4 (linked
   * coils), wrapped (pocketed coils), base. "pt" after a kind puts that layer in the pillow top.
   * ================================================================================== */
  var cutN = 0;
  function n1(v) { return Math.round(v * 10) / 10; }
  draws.serta = function (version, inches, kinds) {
    var lis = version ? Array.prototype.slice.call(version.querySelectorAll('.mo-lineup__layers li')) : [];
    var src = lis.length ? lis.map(function (li) { return { k: (li.getAttribute('data-kind') || '').trim(), t: li.textContent || '' }; })
      : kinds.map(function (k) { return { k: k, t: '' }; });
    var layers = src.map(function (x) {
      var bits = x.k.split(/\s+/), m = /(\d+(?:\.\d+)?)\s*in\./i.exec(x.t);
      return { kind: bits[0], pt: bits.indexOf('pt') > 0, inch: m ? parseFloat(m[1]) : 0 };
    }).filter(function (l) { return l.kind; });
    var id = 'mo-scut-' + (++cutN);
    var VW = 204, VH = 118, X0 = 12, X1 = 198, U = 5, FLOOR = 107, BOTTOM = FLOOR - 0.5;
    var h = Math.max(48, Math.min(inches * U, 84)), top = BOTTOM - h;
    var CUT = X0 + (X1 - X0) * 0.27, slope = 0.16;
    function cx(y) { return CUT + (y - top) * slope; }
    var cover = layers.filter(function (l) { return l.kind === 'cover'; })[0] || { kind: 'cover' };
    var lumbar = layers.some(function (l) { return l.kind === 'lumbar'; });
    var body = layers.filter(function (l) { return l.kind !== 'cover' && l.kind !== 'lumbar'; });
    var coil = body.some(function (l) { return l.kind === 'wrapped' || l.kind === 'q4'; });
    var COV = 2.4, room = h - COV, total = 0;
    body.forEach(function (l) { l.hh = (l.inch || (l.kind === 'fiber' ? 0.5 : 0.75)) * U; total += l.hh; });
    var fit = room / (total || 1);
    body.forEach(function (l) { l.hh *= fit; });
    var ptH = 0;
    body.forEach(function (l) { if (l.pt) ptH += l.hh; });
    var quiltH = 0;
    body.forEach(function (l) { if (l.kind === 'quilt') quiltH += l.hh; });

    var o = '<defs><linearGradient id="' + id + 'g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".3"/><stop offset=".5" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".06"/></linearGradient>' +
      '<clipPath id="' + id + 'c"><path d="M' + n1(cx(top)) + ',' + n1(top) + 'H' + (X1 - 3) + 'Q' + X1 + ',' + n1(top) + ' ' + X1 + ',' + n1(top + 3) +
      'V' + n1(BOTTOM - 2) + 'Q' + X1 + ',' + n1(BOTTOM) + ' ' + (X1 - 2) + ',' + n1(BOTTOM) + 'H' + n1(cx(BOTTOM)) + 'Z"/></clipPath></defs>';
    o += '<ellipse class="s-shadow" cx="' + ((X0 + X1) / 2) + '" cy="' + (FLOOR + 2.2) + '" rx="' + ((X1 - X0) / 2 + 4) + '" ry="2.2"/>';
    o += '<path class="s-dim" d="M5,' + n1(top) + 'V' + n1(BOTTOM) + 'M2.4,' + n1(top) + 'h5.2M2.4,' + n1(BOTTOM) + 'h5.2"/>';

    /* the cut face, layer by layer; everything above the springs lifts apart when the list opens */
    o += '<g clip-path="url(#' + id + 'c)">';
    var y = top + COV, n = body.length;
    body.forEach(function (l, idx) {
      var y0 = y, y1 = y + l.hh, k = l.kind, L = (n - idx) * 3, xs = cx(y0) + 2.5, xe = X1 - 2.5, yy, xx, r;
      var lifts = !/^(q4|wrapped|base|core|core-edge)$/.test(k);
      o += '<g class="' + (lifts ? 'c-lift ' : '') + 's-l s-' + k + (l.pt ? ' is-pt' : '') + '"' + (lifts ? ' style="--lift:' + n1(L) + '"' : '') + '>';
      o += '<rect class="s-fill" x="' + X0 + '" y="' + n1(y0) + '" width="' + (X1 - X0) + '" height="' + n1(Math.max(0.6, l.hh - 0.4)) + '"/>';
      var mid = (y0 + y1) / 2;
      if (k === 'quilt') {
        /* quilted foam: puffs between stitches along the top */
        var pq = 'M' + n1(xs) + ',' + n1(y0 + 1.6);
        for (xx = xs; xx < xe - 7; xx += 7) pq += 'Q' + n1(xx + 3.5) + ',' + n1(y0 - 0.6) + ' ' + n1(xx + 7) + ',' + n1(y0 + 1.6);
        o += '<path class="s-puff" d="' + pq + '"/>';
        for (xx = xs + 7; xx < xe - 2; xx += 7) o += '<circle class="s-knot" cx="' + n1(xx) + '" cy="' + n1(y0 + 1.7) + '" r=".6"/>';
      }
      if (k === 'fiber' || k === 'wool') {
        for (yy = y0 + 1.1; yy < y1 - 0.6; yy += 1.6) {
          var fl = 'M' + n1(xs) + ',' + n1(yy), up = 1;
          for (xx = xs; xx < xe - 4; xx += 4, up = -up) fl += 'Q' + n1(xx + 2) + ',' + n1(yy - up * (k === 'wool' ? 1.1 : 0.6)) + ' ' + n1(xx + 4) + ',' + n1(yy);
          o += '<path class="s-fib" d="' + fl + '"/>';
        }
      }
      if (k === 'aire') {
        /* convoluted foam: two halves meeting in waves, with air in the hollows */
        var amp = Math.min(1.8, l.hh * 0.28), wv = 'M' + n1(xs) + ',' + n1(mid), wv2 = '';
        for (xx = xs, up = 1; xx < xe - 5; xx += 5, up = -up) {
          wv += 'Q' + n1(xx + 2.5) + ',' + n1(mid - up * amp * 2) + ' ' + n1(xx + 5) + ',' + n1(mid);
          if (up < 0) wv2 += '<ellipse class="s-air" cx="' + n1(xx + 2.5) + '" cy="' + n1(mid + amp * 0.55) + '" rx="1.5" ry="' + n1(Math.max(0.35, amp * 0.42)) + '"/>';
        }
        o += wv2 + '<path class="s-wave" d="' + wv + '"/>';
      }
      if (k === 'gel') {
        for (yy = y0 + 1.4, r = 0; yy < y1 - 0.8; yy += 2.6, r++)
          for (xx = xs + (r % 2) * 2.4; xx < xe; xx += 4.8) o += '<circle class="s-bead" cx="' + n1(xx) + '" cy="' + n1(yy) + '" r=".55"/>';
      }
      if (k === 'twist') {
        /* CoolTwist: cooling gel swirled through the memory foam */
        var nsw = l.hh > 6 ? 2 : 1;
        for (var sw = 0; sw < nsw; sw++) {
          var sy = y0 + l.hh * (nsw > 1 ? 0.33 + sw * 0.34 : 0.5), sp = 'M' + n1(xs) + ',' + n1(sy), ph = sw * 1.7;
          for (xx = xs; xx < xe; xx += 2) sp += 'L' + n1(xx) + ',' + n1(sy + Math.sin(xx * 0.21 + ph) * Math.min(1.6, l.hh * 0.14) + Math.sin(xx * 0.07 + ph * 2) * Math.min(0.9, l.hh * 0.07));
          o += '<path class="s-swirl" d="' + sp + '"/>';
        }
      }
      if (k === 'fuze' || k === 'supreme' || k === 'graphite' || k === 'deep') {
        var stp = k === 'deep' ? 3.4 : 2.9;
        for (yy = y0 + 1.2, r = 0; yy < y1 - 0.6; yy += stp * 0.72, r++)
          for (xx = xs + (r % 2) * stp / 2; xx < xe; xx += stp) {
            var jit = ((xx * 13.7 + yy * 7.3) % 1.6) - 0.8;
            o += '<circle class="s-speck" cx="' + n1(xx + jit) + '" cy="' + n1(yy) + '" r="' + (k === 'deep' ? '.38' : '.46') + '"/>';
          }
      }
      if (k === 'comfort' || k === 'support' || k === 'balance' || k === 'core' || k === 'core-edge' || k === 'base') {
        var ps = k === 'core' || k === 'core-edge' ? 4.4 : k === 'base' ? 5.6 : 5;
        for (yy = y0 + 1.8, r = 0; yy < y1 - 1; yy += ps * 0.72, r++)
          for (xx = xs + (r % 2) * ps / 2 + 1; xx < xe; xx += ps) o += '<circle class="s-pore" cx="' + n1(xx) + '" cy="' + n1(yy) + '" r="' + n1(0.4 + 0.16 * (((xx * 7 + yy * 3) | 0) % 3) / 2) + '"/>';
        if (k === 'core-edge') o += '<rect class="s-rail" x="' + (X1 - 7.5) + '" y="' + n1(y0 + 0.4) + '" width="6" height="' + n1(l.hh - 1.2) + '" rx="1"/>';
      }
      if (k === 'zones') {
        /* five zones down the bed: head, shoulders, the firmer middle, hips, legs */
        var zw = xe - xs, parts = [0.16, 0.18, 0.32, 0.18, 0.16], zx = xs, dens = [5, 3.6, 2.4, 3.6, 5];
        parts.forEach(function (f, zi) {
          var w2 = zw * f, zr = 0;
          o += '<rect class="s-zone' + (zi === 2 ? ' is-firm' : zi % 2 ? ' is-alt' : '') + '" x="' + n1(zx + 0.6) + '" y="' + n1(y0 + 0.7) + '" width="' + n1(w2 - 1.2) + '" height="' + n1(Math.max(1, l.hh - 1.8)) + '" rx="1.4"/>';
          for (yy = y0 + 2; yy < y1 - 1.2; yy += dens[zi] * 0.75, zr++)
            for (var zxx = zx + 2 + (zr % 2) * dens[zi] / 2; zxx < zx + w2 - 1.4; zxx += dens[zi]) o += '<circle class="s-zcell" cx="' + n1(zxx) + '" cy="' + n1(yy) + '" r=".45"/>';
          zx += w2;
        });
      }
      if (k === 'q4' || k === 'wrapped') {
        var count = 10, rail = 7, pitch = (xe - xs - rail) / count, c0;
        for (var ci = 0; ci < count; ci++) {
          c0 = xs + pitch * (ci + 0.5);
          var py0 = y0 + 0.8, py1 = y1 - 0.8;
          if (k === 'wrapped') o += '<rect class="s-pocket" x="' + n1(c0 - pitch * 0.46) + '" y="' + n1(py0) + '" width="' + n1(pitch * 0.92) + '" height="' + n1(py1 - py0) + '" rx="2.2"/>';
          var turns = 5, span = py1 - py0 - 2.4, rxc = pitch * (k === 'q4' ? 0.36 : 0.32), ryc = Math.min(1.1, span / turns * 0.3), back = '', front = '', on = -1, steps = turns * 16;
          for (var j = 0; j <= steps; j++) {
            var an = j / steps * turns * Math.PI * 2, hx = c0 + Math.cos(an) * rxc, hy = py0 + 1.2 + span * j / steps + Math.sin(an) * ryc, near = Math.sin(an) >= 0 ? 1 : 0;
            var pt2 = n1(hx) + ',' + n1(hy);
            if (near !== on) { if (near) front += 'M' + pt2; else back += 'M' + pt2; on = near; }
            else if (near) front += 'L' + pt2; else back += 'L' + pt2;
          }
          o += '<path class="s-coil s-coil--back" d="' + back + '"/><path class="s-coil" d="' + front + '"/>';
        }
        if (k === 'q4') {
          /* the 4-in-1 link: a lacing wire along the top and bottom ties the coils together */
          [y0 + 1.6, y1 - 1.6].forEach(function (ly) {
            var lc = 'M' + n1(xs) + ',' + n1(ly);
            for (xx = xs; xx < xe - rail - 1; xx += 1.5) lc += 'L' + n1(xx) + ',' + n1(ly + Math.sin(xx * 2.2) * 0.7);
            o += '<path class="s-lace" d="' + lc + '"/>';
          });
        }
        /* BestEdge: a foam rail round the coils */
        o += '<rect class="s-rail" x="' + n1(xe - rail + 0.5) + '" y="' + n1(y0 + 0.5) + '" width="' + n1(rail - 0.5) + '" height="' + n1(l.hh - 1) + '" rx="1"/>';
      }
      if (!/^(q4|wrapped)$/.test(k)) o += '<rect x="' + X0 + '" y="' + n1(y0) + '" width="' + (X1 - X0) + '" height="' + n1(Math.max(0.6, l.hh - 0.4)) + '" fill="url(#' + id + 'g)"/>';
      o += '<path class="s-seam" d="M' + n1(cx(y1)) + ',' + n1(y1 - 0.2) + 'H' + X1 + '"/>';
      o += '</g>';
      y = y1;
    });
    /* the lumbar band: firmer gel memory foam across the middle third, right above the springs */
    if (lumbar) {
      var above = null, yb = top + COV;
      body.forEach(function (l) { if (l.kind === 'wrapped' || l.kind === 'q4') above = yb; yb += l.hh; });
      if (above != null) {
        var lx0 = X0 + (X1 - X0) / 3, lx1 = X0 + (X1 - X0) * 2 / 3;
        o += '<g class="c-lift" style="--lift:3"><rect class="s-lumbar" x="' + n1(Math.max(lx0, cx(above) + 2)) + '" y="' + n1(above - 3.2) + '" width="' + n1(lx1 - Math.max(lx0, cx(above) + 2)) + '" height="2.8" rx="1.2"/></g>';
      }
    }
    /* the cover over the cut: a thin knit lid that lifts with the layers */
    o += '<g class="c-lift s-l s-cover" style="--lift:' + n1(n * 3 + 4) + '">';
    o += '<path class="s-fill" d="M' + X0 + ',' + n1(top + 0.6) + 'Q' + X0 + ',' + n1(top) + ' ' + (X0 + 2) + ',' + n1(top) + 'H' + (X1 - 3) + 'Q' + X1 + ',' + n1(top) + ' ' + X1 + ',' + n1(top + 2) + 'V' + n1(top + COV - 0.3) + 'H' + X0 + 'Z"/>';
    var kn = 'M' + n1(cx(top) + 2) + ',' + n1(top + 1.3);
    for (var kx = cx(top) + 2, up2 = 1; kx < X1 - 4; kx += 2.6, up2 = -up2) kn += 'Q' + n1(kx + 1.3) + ',' + n1(top + 1.3 - up2 * 0.7) + ' ' + n1(kx + 2.6) + ',' + n1(top + 1.3);
    o += '<path class="s-knit" d="' + kn + '"/>';
    o += '</g>';
    o += '</g>';

    /* the finished side, left of the cut: a pillow top's crown, or a quilted or knit top band,
       then the border: deep blue with a light ring stitch on the coil beds, slate knit on iComfort */
    var band;
    if (ptH > 0) {
      band = top + COV + ptH;
      o += '<path class="s-crown" d="M' + (X0 + 1) + ',' + n1(band) + 'C' + (X0 - 1.2) + ',' + n1(band - ptH * 0.35) + ' ' + (X0 - 1.2) + ',' + n1(top + 3) + ' ' + (X0 + 2.6) + ',' + n1(top) + 'H' + n1(cx(top)) + 'L' + n1(cx(band)) + ',' + n1(band) + 'Z"/>';
      o += '<path class="s-crown-stitch" d="M' + (X0 + 2.5) + ',' + n1(top + (band - top) * 0.52) + 'H' + n1(cx(top + (band - top) * 0.52) - 1) + '"/>';
    } else {
      band = top + COV + (quiltH || 2.2);
      o += '<path class="s-topband' + (quiltH ? ' is-quilt' : '') + '" d="M' + (X0 + 2) + ',' + n1(top) + 'H' + n1(cx(top)) + 'L' + n1(cx(band)) + ',' + n1(band) + 'H' + X0 + 'V' + n1(top + 2) + 'Q' + X0 + ',' + n1(top) + ' ' + (X0 + 2) + ',' + n1(top) + 'Z"/>';
      if (quiltH) o += '<path class="s-crown-stitch" d="M' + (X0 + 2.5) + ',' + n1(top + (band - top) * 0.55) + 'H' + n1(cx(top + (band - top) * 0.55) - 1) + '"/>';
    }
    var pb = band + (ptH > 0 ? 0.8 : 0);
    o += '<path class="s-panel' + (coil ? '' : ' is-foam') + '" d="M' + X0 + ',' + n1(pb) + 'H' + n1(cx(pb)) + 'L' + n1(cx(BOTTOM)) + ',' + n1(BOTTOM) + 'H' + (X0 + 3) + 'Q' + X0 + ',' + n1(BOTTOM) + ' ' + X0 + ',' + n1(BOTTOM - 3) + 'Z"/>';
    if (coil) {
      for (var ry = pb + 4.2, rr = 0; ry < BOTTOM - 2.5; ry += 5.2, rr++)
        for (var rx = X0 + 3.4 + (rr % 2) * 3; rx < cx(ry) - 2.4; rx += 6) o += '<circle class="s-ring" cx="' + n1(rx) + '" cy="' + n1(ry) + '" r="1.6"/>';
    } else {
      for (var bx = X0 + 3.2; bx < cx(BOTTOM) - 1.5; bx += 3.2) {
        var rt = Math.max(pb + 1.2, top + (bx - CUT) / slope + 1);
        if (rt < BOTTOM - 2) o += '<path class="s-rib" d="M' + n1(bx) + ',' + n1(rt) + 'V' + n1(BOTTOM - 1.4) + '"/>';
      }
      o += '<path class="s-stripe" d="M' + (X0 + 0.6) + ',' + n1(pb + 2.4) + 'H' + n1(cx(pb + 2.4) - 0.4) + '"/>';
    }
    o += '<path class="s-tape" d="M' + (X0 + 0.5) + ',' + n1(pb) + 'H' + n1(cx(pb) - 0.3) + 'M' + (X0 + 1.5) + ',' + n1(BOTTOM - 0.6) + 'H' + n1(cx(BOTTOM - 0.6) - 0.3) + '"/>';
    o += '<path class="s-edge" d="M' + n1(cx(top)) + ',' + n1(top) + 'L' + n1(cx(BOTTOM)) + ',' + n1(BOTTOM) + '"/>';
    o += '<path class="s-floor" d="M1,' + (FLOOR + 0.6) + 'H' + (VW - 1) + '"/>';
    return '<svg viewBox="0 0 ' + VW + ' ' + VH + '" aria-hidden="true" focusable="false">' + o + '</svg>';
  };
})();
